// API service layer for Clinic Management System
// All endpoints aligned with backend controllers

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// ==================== SERVER-OUTAGE DETECTION ====================
// Two signs that the server may be gone: a fetch that never received an HTTP response rejects
// with a TypeError (connection refused, network gone), and behind nginx a stopped backend is
// answered for by nginx itself with a 502 or 504. Either way we confirm with one lightweight
// /health ping; if the ping fails too, the whole server is unreachable and the app shows the
// full-page ServerDownScreen instead of every module erroring on its own.

/**
 * One cheap unauthenticated reachability probe: true only when the health check answers with a
 * success. Any reply is not enough: behind nginx a stopped backend still gets one (a 502 from
 * nginx), and a 503 means the backend is up without its database. Never answered from the
 * browser cache, so a retry always reaches the server.
 */
export const pingServer = async () => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);
  try {
    const response = await fetch(`${API_BASE_URL}/health`, { signal: controller.signal, cache: 'no-store' });
    return response.ok;
  } catch (e) {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Which copy of the system this is ('qa', 'prod' or 'dev'), from the public health check, or null
 * when the server does not say. The QA screens show a warning strip.
 */
export const fetchEnvironment = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    const data = await response.json();
    return typeof data?.environment === 'string' ? data.environment : null;
  } catch (e) {
    return null;
  }
};

let outageCheckInFlight = false;
const reportPossibleOutage = async () => {
  // Many requests fail at once when the server drops; verify and dispatch only once.
  if (outageCheckInFlight) return;
  outageCheckInFlight = true;
  try {
    const reachable = await pingServer();
    if (!reachable) window.dispatchEvent(new Event('clinic:server-unreachable'));
  } finally {
    outageCheckInFlight = false;
  }
};

// Replies that stand in for a backend that is not there: nginx answers 502 when it cannot connect
// to it and 504 when it stopped answering, and 503 is the health check's "up, but no database".
// The backend itself sends none of them to refuse a request.
const OUTAGE_STATUSES = [502, 503, 504];

/**
 * fetch() for every request to the API, watching for a server that has gone: no reply at all, or
 * a gateway reply in place of the backend. Without the second, a stopped backend on QA or
 * production never reached the outage screen: every page showed "HTTP error! status: 502" and a
 * refresh opened a sign-in page that could not sign in. The /health check behind
 * reportPossibleOutage keeps one slow or failed request on a healthy server from raising it.
 */
export const serverFetch = async (url, init) => {
  let response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    // An AbortError is a caller cancelling its own request (a superseded search), not an outage.
    if (error?.name === 'TypeError') reportPossibleOutage();
    throw error;
  }
  if (OUTAGE_STATUSES.includes(response.status)) reportPossibleOutage();
  return response;
};

// While this tab changes its password, the server retires the old token before the new one reaches
// the browser. A request that comes back 401 in that moment waits for the change and is sent again.
let tokenChange = null;
const changingToken = async (work) => {
  const pending = (async () => {
    const data = await work();
    if (data?.token) localStorage.setItem('clinic_auth_token', data.token);
    return data;
  })();
  tokenChange = pending;
  try {
    return await pending;
  } finally {
    if (tokenChange === pending) tokenChange = null;
  }
};

// A 401/403 ends the session, with one exception: the request went out with a token this tab has
// since replaced. Changing your password retires every older token (that is how other devices get
// signed out), so a poll that was already on its way fails with the old one while this session is
// fine. Returns true when it signed the user out.
const signOutUnlessTokenReplaced = (tokenUsed) => {
  const current = localStorage.getItem('clinic_auth_token');
  if (tokenUsed && current && current !== tokenUsed) return false;
  localStorage.removeItem('clinic_auth_token');
  localStorage.removeItem('clinic_auth_user');
  window.dispatchEvent(new Event('auth:logout'));
  return true;
};

// Utility function for making API requests
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  // changesToken marks the password change itself, which must never wait for itself below.
  const { changesToken, ...requestOptions } = options;

  // Inject auth token if available
  const token = localStorage.getItem('clinic_auth_token');
  const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...requestOptions.headers,
    },
    ...requestOptions,
  };

  try {
    console.log(`API Request: ${options.method || 'GET'} ${url}`);
    const response = await serverFetch(url, config);

    // Handle 401/403 — auto-logout on auth failure. A request sent with a token that has since
    // been replaced is simply sent again with the current one; the server turned it away at the
    // door, so nothing of it was carried out.
    if (response.status === 401 || response.status === 403) {
      if (tokenChange && !changesToken) {
        await tokenChange.catch(() => {});
        return apiRequest(endpoint, options);
      }
      if (!signOutUnlessTokenReplaced(token)) return apiRequest(endpoint, options);
      throw new Error('Session expired. Please log in again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      // Attach the parsed body + HTTP status to the thrown Error so callers that need to
      // branch on error code (e.g. DUPLICATE_ACTIVE_VISIT vs REVISIT_REASON_REQUIRED) can
      // read it. Existing callers that only look at err.message keep working.
      const err = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      // Services differ in where they put the machine-readable code: the older ones answer
      // {error: 'DUPLICATE_ACTIVE_VISIT'}, the newer ones {error: '<the sentence>', code: 'ENTRY_EXISTS'}.
      // Prefer the explicit `code`, so a caller switching on err.code works for both.
      err.code = errorData.code || errorData.error;
      err.status = response.status;
      err.body = errorData;
      // Same body under the name the billing counter reads (err.data.code, err.data.grandTotal).
      err.data = errorData;
      throw err;
    }

    // Handle empty responses
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (error) {
    // AbortError is expected when callers cancel in-flight requests (e.g. typeahead supersede).
    // A possible outage has already been reported by serverFetch.
    if (error?.name !== 'AbortError') {
      console.error('API request failed:', error);
    }
    throw error;
  }
};

// =============================================
// PATIENT API
// Backend: PatientController.java
// =============================================
export const patientAPI = {
  // GET /api/patients/search?q={query}&page=N&size=M&sortBy=field&sortDir=asc|desc&dateFilter=today|yesterday|week|month
  // Registration-date (createdAt) filtering: pass `dateFilter` (a preset keyword the backend
  // turns into a range) OR an explicit `startDate`/`endDate` ("YYYY-MM-DD") for a custom range.
  // Explicit dates take precedence over the keyword on the backend.
  // Pass `signal` (from AbortController) to cancel an in-flight search when superseded by a newer one.
  search: async (query, { page = 0, size = 20, sortBy, sortDir, dateFilter, startDate, endDate, signal } = {}) => {
    const params = new URLSearchParams({ q: query, page: String(page), size: String(size) });
    if (sortBy) params.append('sortBy', sortBy);
    if (sortDir) params.append('sortDir', sortDir);
    if (dateFilter) params.append('dateFilter', dateFilter);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return apiRequest(`/patients/search?${params.toString()}`, { signal });
  },

  // GET /api/patients/{id}
  getById: async (id) => {
    return apiRequest(`/patients/${id}`);
  },

  // POST /api/patients
  create: async (patientData) => {
    return apiRequest('/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
  },

  // POST /api/patients/lookalikes - existing patients the registration form resembles so far.
  // Called as fields are completed; a POST only because the partial form is the body.
  lookalikes: async (partialForm) => {
    return apiRequest('/patients/lookalikes', {
      method: 'POST',
      body: JSON.stringify(partialForm),
    });
  },

  // PUT /api/patients/{id}
  update: async (id, patientData) => {
    return apiRequest(`/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patientData),
    });
  },

  // DELETE /api/patients/{id}
  delete: async (id) => {
    return apiRequest(`/patients/${id}`, {
      method: 'DELETE',
    });
  },

  // POST /api/patients/{id}/cnic-document (multipart upload — CNIC scan/photo). Front by default;
  // pass side='back' for the back of the card, and target='guardian' for the guardian's CNIC
  // (minor patients) — defaults to the patient's own CNIC.
  uploadCnicDocument: async (id, file, side = 'front', target = 'patient') =>
    userMultipartUpload(
      `${API_BASE_URL}/patients/${id}/cnic-document?side=${side}&target=${target}`,
      file,
    ),

  // POST /api/patients/{id}/profile-image (multipart upload — JPG/PNG/WebP)
  uploadProfileImage: async (id, file) =>
    userMultipartUpload(`${API_BASE_URL}/patients/${id}/profile-image`, file),

  // DELETE /api/patients/{id}/profile-image (remove the profile picture)
  deleteProfileImage: async (id) =>
    apiRequest(`/patients/${id}/profile-image`, { method: 'DELETE' }),

  // GET /api/patients/{id}/document-versions (upload history of single-slot docs)
  getDocumentVersions: async (id) => apiRequest(`/patients/${id}/document-versions`),

  // GET /api/patients?page={page}&size={size}
  getAll: async (page = 0, size = 20) => {
    return apiRequest(`/patients?page=${page}&size=${size}`);
  },

  // GET /api/patients/stats
  getStats: async () => {
    return apiRequest('/patients/stats');
  },

  // GET /api/patients/{id}/visits?page={page}&size={size}
  getVisitHistory: async (patientId, page = 0, size = 20) => {
    return apiRequest(`/patients/${patientId}/visits?page=${page}&size=${size}`);
  },

  // GET /api/patients/{id}/guardians (the adults on a child's file, primary first)
  getGuardians: async (id) => apiRequest(`/patients/${id}/guardians`),

  // POST /api/patients/{id}/guardians (another adult on the child's file)
  addGuardian: async (id, guardian) =>
    apiRequest(`/patients/${id}/guardians`, { method: 'POST', body: JSON.stringify(guardian) }),

  // PUT /api/patients/{id}/guardians/{guardianId}/primary
  makeGuardianPrimary: async (id, guardianId) =>
    apiRequest(`/patients/${id}/guardians/${guardianId}/primary`, { method: 'PUT' }),

  // DELETE /api/patients/{id}/guardians/{guardianId}
  removeGuardian: async (id, guardianId) =>
    apiRequest(`/patients/${id}/guardians/${guardianId}`, { method: 'DELETE' }),

  // POST /api/patients/lookalikes/decisions (a pair judged two people leaves the duplicates report)
  clearLookalikePair: async (patientId, otherPatientId, note) =>
    apiRequest('/patients/lookalikes/decisions', {
      method: 'POST',
      body: JSON.stringify({ patientId, otherPatientId, note: note || null }),
    }),

  // DELETE /api/patients/lookalikes/decisions/{decisionId} (put the pair back on the report)
  restoreLookalikePair: async (decisionId) =>
    apiRequest(`/patients/lookalikes/decisions/${decisionId}`, { method: 'DELETE' }),

  // POST /api/patients/merge (two files that are one person become one; the merged MRN still resolves)
  merge: async (keepId, mergeId, note) =>
    apiRequest('/patients/merge', {
      method: 'POST',
      body: JSON.stringify({ keepId, mergeId, note: note || null }),
    }),

  // GET /api/patients/{id}/merges (the files folded into this one)
  getMerges: async (id) => apiRequest(`/patients/${id}/merges`),
};

// =============================================
// VISIT/TOKEN API
// Backend: VisitController.java
// =============================================
export const visitAPI = {
  // GET /api/visits - Get all visits (with optional filters)
  // Filters: doctorId, status, date
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.doctorId && filters.doctorId !== 'all') params.append('doctorId', filters.doctorId);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.date) params.append('date', filters.date);
    // fromDate + toDate together return every visit in the range (used by OPD Receipts).
    // The backend short-circuits the other filters when both are present.
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);

    const queryString = params.toString();
    return apiRequest(`/visits${queryString ? `?${queryString}` : ''}`);
  },

  // GET /api/visits/{id}
  getById: async (id) => {
    return apiRequest(`/visits/${id}`);
  },

  // POST /api/visits - Create new visit/token
  create: async (visitData, paymentProofFiles = []) =>
    withPaymentProof('/visits', visitData, paymentProofFiles),

  // PUT /api/visits/{id} - Correct an already-issued token (doctor, service, discount,
  // payment mode, emergency). Every field is optional; omitted fields are left alone.
  // Rejected with 409 VISIT_NOT_EDITABLE once the patient has been called for vitals or by the
  // doctor, or when the token consumed patient credit. Changing the doctor renumbers the token
  // into that doctor's sequence, so the caller should offer a reprint afterwards.
  update: async (id, visitData) => {
    return apiRequest(`/visits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(visitData),
    });
  },

  // GET /api/visits/{id}/audit - Change history for a token, newest first.
  getAudit: async (id) => {
    return apiRequest(`/visits/${id}/audit`);
  },

  // GET /api/visits/today - Get today's visits only
  getTodaysVisits: async (doctorId = null) => {
    const endpoint = doctorId ? `/visits/today?doctorId=${doctorId}` : '/visits/today';
    return apiRequest(endpoint);
  },

  // GET /api/visits/stats - Get visit statistics for today
  getStats: async (doctorId = null) => {
    const endpoint = doctorId ? `/visits/stats?doctorId=${doctorId}` : '/visits/stats';
    return apiRequest(endpoint);
  },

  // Alias for backward compatibility
  getQueueStats: async (doctorId = null) => {
    return visitAPI.getStats(doctorId);
  },

  // PUT /api/visits/{id}/call - Call token (change status to IN_PROGRESS)
  callToken: async (id) => {
    return apiRequest(`/visits/${id}/call`, {
      method: 'PUT',
    });
  },

  // PUT /api/visits/{id}/complete - Complete visit
  completeVisit: async (id, notes = '') => {
    return apiRequest(`/visits/${id}/complete`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  },

  // PATCH /api/visits/{id}/cancel - Cancel visit
  // feeOutcome is REFUND / CREDIT / KEEP and is required by the server whenever a fee was paid:
  // cancelling used to leave that money unaccounted for. Applied patient credit is returned by
  // the server on every cancellation and needs no instruction from here.
  cancelVisit: async (id, reason = '', feeOutcome = null) => {
    return apiRequest(`/visits/${id}/cancel`, {
      method: 'PATCH',
      body: JSON.stringify({ reason, feeOutcome }),
    });
  },

  // Alias for cancelVisit
  cancel: async (id, reason = '', feeOutcome = null) => {
    return visitAPI.cancelVisit(id, reason, feeOutcome);
  },

  // PATCH /api/visits/{id}/status - Update visit status
  updateStatus: async (id, status) => {
    return apiRequest(`/visits/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  // GET /api/visits/queue - Doctor's queue (READY_FOR_DOCTOR only)
  getQueue: async () => {
    return apiRequest('/visits/queue');
  },

  // GET /api/visits/nurse-queue - Nurse queue (PENDING_VITALS)
  getNurseQueue: async () => {
    return apiRequest('/visits/nurse-queue');
  },

  // GET /api/visits/patient/{patientId}/history-with-rx
  // Used by the Patient Details "Visits" tab — full history with prescription status rolled up.
  getPatientHistoryWithRx: async (patientId) =>
    apiRequest(`/visits/patient/${patientId}/history-with-rx`),

  // GET /api/visits/patient/{patientId}/vitals-history
  // Used by the Patient Details "Vitals" tab — every visit where the nurse recorded vitals.
  getPatientVitalsHistory: async (patientId) =>
    apiRequest(`/visits/patient/${patientId}/vitals-history`),

  // GET /api/visits/patient/{patientId}/today-status
  // Returns { hasActiveVisit, activeTokenNumber, activeStatus, hasCompletedVisit }
  // Used by the Issue-Token UI to (a) block duplicate active tokens and
  // (b) require a revisit reason after a completed visit on the same day.
  getPatientTodayStatus: async (patientId) => {
    return apiRequest(`/visits/patient/${patientId}/today-status`);
  },

  // GET /api/visits/nurse-queue/recorded-today - Today's recorded vitals (for review/amend)
  getRecordedToday: async () => {
    return apiRequest('/visits/nurse-queue/recorded-today');
  },

  // POST /api/visits/{id}/vitals - Record vitals for a visit.
  // Transitions visit from PENDING_VITALS to READY_FOR_DOCTOR once mandatory vitals are saved.
  recordVitals: async (visitId, vitals, recordedBy = null) => {
    const query = recordedBy ? `?recordedBy=${encodeURIComponent(recordedBy)}` : '';
    return apiRequest(`/visits/${visitId}/vitals${query}`, {
      method: 'POST',
      body: JSON.stringify(vitals),
    });
  },

  // GET /api/visits/queue/summary - Get queue summary
  getQueueSummary: async () => {
    return apiRequest('/visits/queue/summary');
  },
};

// =============================================
// DOCTOR API
// Backend: UserController.java (doctors endpoint)
// =============================================
export const doctorAPI = {
  // GET /api/users/doctors
  getAll: async () => {
    return apiRequest('/users/doctors');
  },

  // GET /api/users/{id}
  getById: async (id) => {
    return apiRequest(`/users/${id}`);
  },
};

// =============================================
// MEDICAL SERVICES API
// Backend: MedicalServiceController.java
// =============================================
export const medicalServiceAPI = {
  // GET /api/medical-services (active only — used by token issue dropdown).
  // Pass { generalOnly: true } to filter out doctor-specific rows (used by Add Doctor form).
  getAll: async (opts = {}) => {
    const qs = opts.generalOnly ? '?generalOnly=true' : '';
    return apiRequest(`/medical-services${qs}`);
  },

  // GET /api/medical-services/all (all services including inactive — used by management screen)
  getAllIncludingInactive: async () => {
    return apiRequest('/medical-services/all');
  },

  // GET /api/medical-services/for-doctor/{doctorId} — services available when issuing a token
  // for this doctor: general services + services tied to this doctor.
  getForDoctor: async (doctorId) => {
    return apiRequest(`/medical-services/for-doctor/${doctorId}`);
  },

  // Arranges one doctor's whole dropdown (their own services and the general ones together).
  // Only that doctor is affected — a general service can sit first for them and last for another.
  reorderForDoctor: async (doctorId, orderedIds) =>
    apiRequest('/medical-services/reorder-for-doctor', {
      method: 'PUT',
      body: JSON.stringify({ doctorId, orderedIds }),
    }),

  // Service ids this doctor's dropdown has been arranged into; empty means the default order.
  getDoctorOrder: async (doctorId) => apiRequest(`/medical-services/doctor-order/${doctorId}`),

  clearDoctorOrder: async (doctorId) =>
    apiRequest(`/medical-services/doctor-order/${doctorId}`, { method: 'DELETE' }),

  // GET /api/medical-services/{id}
  getById: async (id) => {
    return apiRequest(`/medical-services/${id}`);
  },

  // POST /api/medical-services
  create: async (serviceData) => {
    return apiRequest('/medical-services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  },

  // PUT /api/medical-services/{id}
  update: async (id, serviceData) => {
    return apiRequest(`/medical-services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  },

  // PUT /api/medical-services/reorder — persist the drag order for ONE scope at a time
  // (a single doctor's services, or the general set). The token-issue query partitions by doctor
  // before display order, so scopes can never interleave.
  reorder: async (orderedIds) => {
    return apiRequest('/medical-services/reorder', {
      method: 'PUT',
      body: JSON.stringify({ orderedIds }),
    });
  },

  // DELETE /api/medical-services/{id} (soft delete — deactivates)
  deactivate: async (id) => {
    return apiRequest(`/medical-services/${id}`, {
      method: 'DELETE',
    });
  },

  // POST /api/medical-services/{id}/activate
  activate: async (id) => {
    return apiRequest(`/medical-services/${id}/activate`, {
      method: 'POST',
    });
  },
};

// =============================================
// TAG API
// Backend: TagController.java
// =============================================
export const tagAPI = {
  // GET /api/tags — all ACTIVE tags (used by the patient form to populate the picker).
  getAll: async () => apiRequest('/tags'),
  // GET /api/tags/all — all tags including inactive (used by the Settings → Tags page).
  getAllIncludingInactive: async () => apiRequest('/tags/all'),
  // GET /api/tags/search?q={query} — case-insensitive search for autocomplete
  search: async (query, { page = 0, size = 10 } = {}) => {
    const params = new URLSearchParams({ q: query || '', page: String(page), size: String(size) });
    return apiRequest(`/tags/search?${params.toString()}`);
  },
  create: async (tagData) =>
    apiRequest('/tags', { method: 'POST', body: JSON.stringify(tagData) }),
  update: async (id, tagData) =>
    apiRequest(`/tags/${id}`, { method: 'PUT', body: JSON.stringify(tagData) }),
  deactivate: async (id) => apiRequest(`/tags/${id}`, { method: 'DELETE' }),
  reactivate: async (id) => apiRequest(`/tags/${id}/reactivate`, { method: 'POST' }),
};

// =============================================
// INSTRUCTION CONFIG API (Settings → Patient Instructions)
// type: 'PATIENT_INSTRUCTION' | 'MEDICATION_INSTRUCTION'
// =============================================
// specialityId: '' or null = the General ("all specialities") scope; a UUID scopes to that speciality.
export const instructionAPI = {
  list: async (type, specialityId) =>
    apiRequest(`/instructions?type=${type}${specialityId ? `&specialityId=${specialityId}` : ''}`),
  create: async (data) => apiRequest('/instructions', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/instructions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: async (id) => apiRequest(`/instructions/${id}`, { method: 'DELETE' }),
  // Persist a new display order for a type within one speciality scope. orderedIds = full list of
  // that scope's ids in the desired order; the backend sets each row's displayOrder by index so the
  // chips appear in this order in every prescription picker.
  reorder: async (type, orderedIds, specialityId) =>
    apiRequest('/instructions/reorder', {
      method: 'PUT',
      body: JSON.stringify({ type, orderedIds, specialityId: specialityId || '' }),
    }),
  // Every pending change on one speciality's list in a single transaction. Toggling five rows was
  // five requests that could each fail on their own; this one either lands whole or not at all.
  batch: async (type, specialityId, { activate = [], deactivate = [], remove = [] }) =>
    apiRequest('/instructions/batch', {
      method: 'POST',
      body: JSON.stringify({ type, specialityId: specialityId || '', activate, deactivate, remove }),
    }),
  // Instructions of this type from every speciality, minus what this scope already has — the pool
  // for "Add from library", so wording written once can be reused instead of retyped.
  library: async (type, specialityId) =>
    apiRequest(`/instructions/library?type=${type}${specialityId ? `&specialityId=${specialityId}` : ''}`),
  copy: async (type, specialityId, ids) =>
    apiRequest('/instructions/copy', {
      method: 'POST',
      body: JSON.stringify({ type, specialityId: specialityId || '', ids }),
    }),
  // Library-level edit and delete. A library entry can exist as an independent copy under several
  // specialities, so both act on every copy of that sentence rather than on the one row.
  updateLibraryEntry: async (id, valueEnglish, valueUrdu) =>
    apiRequest(`/instructions/library/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ valueEnglish, valueUrdu }),
    }),
  deleteLibraryEntry: async (id) =>
    apiRequest(`/instructions/library/${id}`, { method: 'DELETE' }),
};

// =============================================
// SPECIALITY API (Settings → Specialities)
// Doctor speciality catalog. Scopes instruction chips and quick lab tests.
// =============================================
// =============================================
// INTAKE API (first-visit medical history)
// forPatient returns { pending, questions, answers, intake } — pending is what makes the
// prescription workspace hold the doctor up until the form is taken.
// =============================================
export const intakeAPI = {
  forPatient: async (patientId) => apiRequest(`/intake/patients/${patientId}`),
  save: async (patientId, payload) =>
    apiRequest(`/intake/patients/${patientId}`, { method: 'POST', body: JSON.stringify(payload) }),
  // Settings -> Intake Questions
  listQuestions: async () => apiRequest('/intake/questions'),
  createQuestion: async (data) =>
    apiRequest('/intake/questions', { method: 'POST', body: JSON.stringify(data) }),
  updateQuestion: async (id, data) =>
    apiRequest(`/intake/questions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  retireQuestion: async (id) => apiRequest(`/intake/questions/${id}`, { method: 'DELETE' }),
};

export const specialityAPI = {
  list: async (includeInactive = false) =>
    apiRequest(`/specialities${includeInactive ? '?includeInactive=true' : ''}`),
  create: async (data) => apiRequest('/specialities', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/specialities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: async (id) => apiRequest(`/specialities/${id}`, { method: 'DELETE' }),
  reorder: async (orderedIds) =>
    apiRequest('/specialities/reorder', { method: 'PUT', body: JSON.stringify({ orderedIds }) }),
};

// =============================================
// QUICK LAB TEST API (Settings → Patient Instructions → Quick Lab Tests)
// Per-speciality quick test chips shown in the prescription Treatment tab.
// specialityId: '' or null = the General list every doctor sees.
// =============================================
export const quickLabTestAPI = {
  list: async (specialityId) =>
    apiRequest(`/quick-lab-tests${specialityId ? `?specialityId=${specialityId}` : ''}`),
  add: async (specialityId, labTestId) =>
    apiRequest('/quick-lab-tests', {
      method: 'POST',
      body: JSON.stringify({ specialityId: specialityId || '', labTestId }),
    }),
  remove: async (id) => apiRequest(`/quick-lab-tests/${id}`, { method: 'DELETE' }),
  // Status toggles and removals commit together, the same way the instruction tabs do it, so a
  // half-applied list is not a state the admin can end up in.
  batch: async (specialityId, { activate = [], deactivate = [], remove = [] }) =>
    apiRequest('/quick-lab-tests/batch', {
      method: 'POST',
      body: JSON.stringify({ specialityId: specialityId || '', activate, deactivate, remove }),
    }),
  reorder: async (specialityId, orderedIds) =>
    apiRequest('/quick-lab-tests/reorder', {
      method: 'PUT',
      body: JSON.stringify({ specialityId: specialityId || '', orderedIds }),
    }),
  // The whole lab/radiology catalog — the picker filters this client-side so every test stays
  // selectable regardless of speciality or category.
  catalog: async () => apiRequest('/quick-lab-tests/catalog'),
  categories: async () => apiRequest('/quick-lab-tests/categories'),
  // Adds a test the catalog doesn't have yet, then puts it on this scope's quick list.
  createTest: async (specialityId, { testName, testNameUrdu, category }) =>
    apiRequest('/quick-lab-tests/new-test', {
      method: 'POST',
      body: JSON.stringify({ specialityId: specialityId || '', testName, testNameUrdu, category }),
    }),
  // Catalogue-level edit and delete. A test is shared by every speciality that picked it, so both
  // of these reach all of them at once. Prescriptions already written keep the name they used.
  updateCatalogTest: async (id, { testName, testNameUrdu, category }) =>
    apiRequest(`/quick-lab-tests/catalog/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ testName, testNameUrdu, category }),
    }),
  deleteCatalogTest: async (id) =>
    apiRequest(`/quick-lab-tests/catalog/${id}`, { method: 'DELETE' }),
};

// =============================================
// PATIENT REPORT NOTE API
// Doctor's free-text notes about reports the patient showed but never uploaded.
// Patient-level, so they stay visible on every later visit. Never printed.
// =============================================
export const patientReportNoteAPI = {
  list: async (patientId) => apiRequest(`/patients/${patientId}/report-notes`),
  create: async (patientId, data) =>
    apiRequest(`/patients/${patientId}/report-notes`, { method: 'POST', body: JSON.stringify(data) }),
  update: async (patientId, noteId, data) =>
    apiRequest(`/patients/${patientId}/report-notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  remove: async (patientId, noteId) =>
    apiRequest(`/patients/${patientId}/report-notes/${noteId}`, { method: 'DELETE' }),
};

// =============================================
// FAMILY API
// Backend: FamilyController.java
// =============================================
export const familyAPI = {
  // GET /api/family/all
  getAll: async () => {
    return apiRequest('/family/all');
  },

  // GET /api/family/detect/{cnic}
  detectFamilyMembers: async (cnic) => {
    return apiRequest(`/family/detect/${cnic}`);
  },

  // POST /api/family/link
  linkFamilyMembers: async (familyHeadId, familyMemberId, relationship) => {
    return apiRequest('/family/link', {
      method: 'POST',
      body: JSON.stringify({ familyHeadId, familyMemberId, relationship }),
    });
  },

  // GET /api/family/{patientId}/tree
  getFamilyTree: async (patientId) => {
    return apiRequest(`/family/${patientId}/tree`);
  },

  // DELETE /api/family/unlink/{familyHeadId}/{memberId}
  unlinkFamilyMember: async (familyHeadId, memberId) => {
    return apiRequest(`/family/unlink/${familyHeadId}/${memberId}`, {
      method: 'DELETE',
    });
  },

  // GET /api/family/search?q={query}
  search: async (query) => {
    return apiRequest(`/family/search?q=${encodeURIComponent(query)}`);
  },

  // GET /api/family/stats
  getStats: async () => {
    return apiRequest('/family/stats');
  },
};

// =============================================
// DASHBOARD API
// Backend: DashboardController.java
// =============================================
export const dashboardAPI = {
  // GET /api/dashboard/overview
  getOverview: async () => {
    return apiRequest('/dashboard/overview');
  },

  // GET /api/dashboard/recent-activity?limit={limit}
  getRecentActivity: async (limit = 10) => {
    return apiRequest(`/dashboard/recent-activity?limit=${limit}`);
  },
};

// Shared multipart upload helper for user-level file endpoints (profile image, PMDC
// certificate, resume). Pulls the auth token, posts FormData, handles 401 like apiRequest.
const userMultipartUpload = async (url, file) => {
  const token = localStorage.getItem('clinic_auth_token');
  const formData = new FormData();
  formData.append('file', file);
  const response = await serverFetch(url, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401 || response.status === 403) {
    if (!signOutUnlessTokenReplaced(token)) throw new Error('Please try again.');
    throw new Error('Session expired. Please log in again.');
  }
  const data = await response.json().catch(() => ({ error: 'Upload failed' }));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to upload file');
  }
  return data;
};


// Stock receipts are sent as multipart so the record and its bill copies commit together. The JSON
// goes in a `payload` part with an explicit application/json type — without the type Spring treats
// it as text/plain and refuses to bind it to @RequestPart.
const receiptMultipart = async (url, method, payload, files, paymentProofFiles) => {
  const token = localStorage.getItem('clinic_auth_token');
  const formData = new FormData();
  formData.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  (files || []).forEach(f => formData.append('bills', f));
  (paymentProofFiles || []).forEach(f => formData.append('paymentProofs', f));
  const response = await serverFetch(url, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401 || response.status === 403) {
    if (!signOutUnlessTokenReplaced(token)) throw new Error('Please try again.');
    throw new Error('Session expired. Please log in again.');
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to save the stock receipt');
  }
  return data;
};


/**
 * POST a JSON payload plus optional payment screenshots as one multipart request, so the record and
 * its proof commit together. With no files it falls back to plain JSON, which is what every cash
 * payment does and keeps those requests exactly as they were.
 */
const withPaymentProof = async (path, payload, files, method = 'POST') => {
  if (!files || files.length === 0) {
    return apiRequest(path, { method, body: JSON.stringify(payload) });
  }
  const token = localStorage.getItem('clinic_auth_token');
  const formData = new FormData();
  // Explicit application/json: without the type Spring binds the part as text/plain and refuses it.
  formData.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  files.forEach(f => formData.append('paymentProofs', f));
  const response = await serverFetch(`${API_BASE_URL}${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401 || response.status === 403) {
    if (!signOutUnlessTokenReplaced(token)) throw new Error('Please try again.');
    throw new Error('Session expired. Please log in again.');
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Carries the status and body like apiRequest does, so a caller can branch on a refusal's code
    // (the billing counter's TOTAL_CHANGED, STOCK_SHORT...) whichever of the two shapes was sent.
    const err = new Error(data.message || data.error || 'Request failed');
    err.status = response.status;
    err.body = data;
    err.data = data;
    throw err;
  }
  return data;
};

// =============================================
// PAYMENT PROOFS
// Backend: PaymentProofController.java
// The transfer screenshot behind a non-cash payment. One set of endpoints for every payment point.
// =============================================
export const paymentProofAPI = {
  // Same reason as stockReceiptAPI.billFileUrl: without the API base this resolves to the app.
  fileUrl: (proofId) => `${API_BASE_URL}/payment-proofs/${proofId}/file`,

  list: async (ownerType, ownerId) =>
    apiRequest(`/payment-proofs?ownerType=${ownerType}&ownerId=${ownerId}`),
  // For payments recorded against something that already exists. Create flows send their files with
  // the record itself so the two commit together.
  attach: async (ownerType, ownerId, paymentMode, files) => {
    const token = localStorage.getItem('clinic_auth_token');
    const formData = new FormData();
    (files || []).forEach(f => formData.append('files', f));
    const qs = `ownerType=${ownerType}&ownerId=${ownerId}${paymentMode ? `&paymentMode=${paymentMode}` : ''}`;
    const response = await serverFetch(`${API_BASE_URL}/payment-proofs?${qs}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || data.error || 'Failed to attach the screenshot');
    return data;
  },
};

// =============================================
// USER MANAGEMENT API
// Backend: UserController.java
// =============================================
export const userAPI = {
  // GET /api/users OR /api/users?role=...&status=...&search=...
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.role && filters.role !== 'ALL') params.append('role', filters.role);
    if (filters.status && filters.status !== 'ALL') params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    
    const queryString = params.toString();
    return apiRequest(`/users${queryString ? `?${queryString}` : ''}`);
  },

  // GET /api/users/{id}
  getById: async (id) => {
    return apiRequest(`/users/${id}`);
  },

  // GET /api/users/username/{username}
  getByUsername: async (username) => {
    return apiRequest(`/users/username/${username}`);
  },

  // POST /api/users
  create: async (userData) => {
    return apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  // PUT /api/users/{id}
  update: async (id, userData) => {
    return apiRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  // PUT /api/users/{id}/doctor-settings — per-doctor token prefix + consultation room.
  // Send empty strings to clear. Returns the updated user (mapUserToResponse shape).
  updateDoctorSettings: async (id, { tokenPrefix, roomNumber, specialityId, nurseOperational, clinicTimings }) => {
    return apiRequest(`/users/${id}/doctor-settings`, {
      method: 'PUT',
      // nurseOperational is per doctor: whether a nurse records their vitals before they see the
      // patient. Undefined leaves it untouched server-side.
      body: JSON.stringify({
        tokenPrefix,
        roomNumber,
        specialityId: specialityId || null,
        nurseOperational,
        // Weekly clinic timings as JSON ('' clears them); undefined leaves them untouched.
        clinicTimings,
      }),
    });
  },

  // PATCH /api/users/{id}/status — accepts reason + description for the audit trail.
  // Effective date is server-fixed to today (the StatusChangeModal renders today read-only,
  // so we don't send it; the backend re-stamps regardless).
  updateStatus: async (id, status, reason = null, description = null) => {
    return apiRequest(`/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason, description }),
    });
  },

  // GET /api/users/{id}/audit-log — full audit timeline for the user (status changes,
  // field edits, password resets, etc.). Newest first.
  getAuditLog: async (id) => {
    return apiRequest(`/users/${id}/audit-log`);
  },

  // POST /api/users/{id}/reset-password
  resetPassword: async (id) => {
    return apiRequest(`/users/${id}/reset-password`, {
      method: 'POST',
    });
  },

  // POST /api/users/{id}/change-password
  // Signs this account out everywhere else; this tab carries on with the token that comes back.
  changePassword: async (id, currentPassword, newPassword) =>
    changingToken(() =>
      apiRequest(`/users/${id}/change-password`, {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
        changesToken: true,
      })
    ),

  // GET /api/users/{id}/effective-permissions
  getEffectivePermissions: async (id) => {
    return apiRequest(`/users/${id}/effective-permissions`);
  },

  // GET /api/users/{id}/permission-overrides
  getPermissionOverrides: async (id) => {
    return apiRequest(`/users/${id}/permission-overrides`);
  },

  // PUT /api/users/{id}/permission-overrides
  updatePermissionOverrides: async (id, overrides) => {
    return apiRequest(`/users/${id}/permission-overrides`, {
      method: 'PUT',
      body: JSON.stringify(overrides),
    });
  },

  // DELETE /api/users/{id}/permission-overrides
  clearPermissionOverrides: async (id) => {
    return apiRequest(`/users/${id}/permission-overrides`, { method: 'DELETE' });
  },

  // POST /api/users/{id}/profile-image (multipart upload)
  uploadProfileImage: async (id, file) => userMultipartUpload(`${API_BASE_URL}/users/${id}/profile-image`, file),

  // DELETE /api/users/{id}/profile-image (remove the profile picture)
  deleteProfileImage: async (id) => apiRequest(`/users/${id}/profile-image`, { method: 'DELETE' }),

  // GET /api/users/{id}/document-versions (upload history of single-slot docs)
  getDocumentVersions: async (id) => apiRequest(`/users/${id}/document-versions`),

  // POST /api/users/{id}/pmdc-certificate (multipart upload — Doctor onboarding)
  uploadPmdcCertificate: async (id, file) =>
    userMultipartUpload(`${API_BASE_URL}/users/${id}/pmdc-certificate`, file),

  // POST /api/users/{id}/resume (multipart upload — Doctor onboarding)
  uploadResume: async (id, file) => userMultipartUpload(`${API_BASE_URL}/users/${id}/resume`, file),

  // POST /api/users/{id}/qualification-file (multipart upload — universal HR doc)
  uploadQualificationFile: async (id, file) =>
    userMultipartUpload(`${API_BASE_URL}/users/${id}/qualification-file`, file),

  // POST /api/users/{id}/contract-agreement (multipart upload — Doctor Contract Agreement)
  uploadContractAgreement: async (id, file) =>
    userMultipartUpload(`${API_BASE_URL}/users/${id}/contract-agreement`, file),

  // POST /api/users/{id}/certifications (multipart upload — Technician certifications)
  uploadCertifications: async (id, file) =>
    userMultipartUpload(`${API_BASE_URL}/users/${id}/certifications`, file),

  // POST /api/users/{id}/cnic-document (multipart upload — CNIC scan/photo, front)
  uploadCnicDocument: async (id, file) =>
    userMultipartUpload(`${API_BASE_URL}/users/${id}/cnic-document`, file),

  // POST /api/users/{id}/cnic-document-back (multipart upload — CNIC back side)
  uploadCnicBackDocument: async (id, file) =>
    userMultipartUpload(`${API_BASE_URL}/users/${id}/cnic-document-back`, file),

  // DELETE /api/users/{id} — soft delete (sets status=DELETED). Accepts a reason +
  // description for the audit trail, same shape as updateStatus.
  delete: async (id, reason = null, description = null) => {
    return apiRequest(`/users/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason, description }),
    });
  },

  // POST /api/users/{id}/restore — bring a soft-deleted user back. Defaults to ACTIVE
  // status server-side; reason + description are written to the audit log.
  restore: async (id, reason = null, description = null) => {
    return apiRequest(`/users/${id}/restore`, {
      method: 'POST',
      body: JSON.stringify({ reason, description }),
    });
  },

  // POST /api/users/{id}/deactivate
  deactivate: async (id) => {
    return apiRequest(`/users/${id}/deactivate`, {
      method: 'POST',
    });
  },

  // GET /api/users/doctors
  getDoctors: async () => {
    return apiRequest('/users/doctors');
  },

  // GET /api/users/stats
  getStats: async () => {
    return apiRequest('/users/stats');
  },

  // GET /api/users/check-username/{username}
  checkUsername: async (username) => {
    return apiRequest(`/users/check-username/${username}`);
  },

  // GET /api/users/check-email?email=...
  checkEmail: async (email) => {
    return apiRequest(`/users/check-email?email=${encodeURIComponent(email)}`);
  },

  // GET /api/users/preview-username?firstName=...&lastName=...
  previewUsername: async (firstName, lastName) => {
    return apiRequest(`/users/preview-username?firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`);
  },
};

// =============================================
// VENDOR API
// Backend: VendorController.java
// =============================================
export const vendorAPI = {
  // GET /api/vendors
  getAll: async () => {
    return apiRequest('/vendors');
  },

  // GET /api/vendors/{id}
  getById: async (id) => {
    return apiRequest(`/vendors/${id}`);
  },

  // GET /api/vendors/status/{status}
  getByStatus: async (status) => {
    return apiRequest(`/vendors/status/${status}`);
  },

  // GET /api/vendors/type/{type}
  getByType: async (type) => {
    return apiRequest(`/vendors/type/${type}`);
  },

  // GET /api/vendors/type/{type}/active
  getActiveByType: async (type) => {
    return apiRequest(`/vendors/type/${type}/active`);
  },

  // GET /api/vendors/search?q={query}
  search: async (query) => {
    return apiRequest(`/vendors/search?q=${encodeURIComponent(query)}`);
  },

  // POST /api/vendors
  create: async (vendorData) => {
    return apiRequest('/vendors', {
      method: 'POST',
      body: JSON.stringify(vendorData),
    });
  },

  // PUT /api/vendors/{id}
  update: async (id, vendorData) => {
    return apiRequest(`/vendors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(vendorData),
    });
  },

  // POST /api/vendors/{id}/suspend
  suspend: async (id, reason) => {
    return apiRequest(`/vendors/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  // POST /api/vendors/{id}/reactivate
  reactivate: async (id) => {
    return apiRequest(`/vendors/${id}/reactivate`, {
      method: 'POST',
    });
  },

  // DELETE /api/vendors/{id}
  delete: async (id) => {
    return apiRequest(`/vendors/${id}`, {
      method: 'DELETE',
    });
  },

  // GET /api/vendors/stats
  getStats: async () => {
    return apiRequest('/vendors/stats');
  },

  // GET /api/vendors/types
  getTypes: async () => {
    return apiRequest('/vendors/types');
  },
};

// =============================================
// MEDICINE API
// Backend: MedicineController.java
// =============================================
export const medicineAPI = {
  // GET /api/medicines
  getAll: async () => {
    return apiRequest('/medicines');
  },

  // GET /api/medicines/with-inventory — medicines list enriched with aggregates across all
  // active stock-receipt lines (batch count, weighted avg / range selling price, earliest
  // expiring batch). Used by the Medicine Inventory table.
  getAllWithInventory: async (category) => {
    // No category = both medicines and supplies, which is what the stock-receipt picker wants.
    return apiRequest(`/medicines/with-inventory${category ? `?category=${category}` : ''}`);
  },

  // GET /api/medicines/{id}
  getById: async (id) => {
    return apiRequest(`/medicines/${id}`);
  },

  // GET /api/medicines/search?q={query}
  search: async (query) => {
    return apiRequest(`/medicines/search?q=${encodeURIComponent(query)}`);
  },

  // GET /api/medicines/status/{status}
  getByStatus: async (status) => {
    return apiRequest(`/medicines/status/${status}`);
  },

  // GET /api/medicines/type/{type}
  getByType: async (type) => {
    return apiRequest(`/medicines/type/${type}`);
  },

  // GET /api/medicines/vendor/{vendorId}
  getByVendor: async (vendorId) => {
    return apiRequest(`/medicines/vendor/${vendorId}`);
  },

  // GET /api/medicines/vendor-name?name={name}
  getByVendorName: async (name) => {
    return apiRequest(`/medicines/vendor-name?name=${encodeURIComponent(name)}`);
  },

  // GET /api/medicines/expired
  getExpired: async () => {
    return apiRequest('/medicines/expired');
  },

  // GET /api/medicines/near-expiry
  getNearExpiry: async () => {
    return apiRequest('/medicines/near-expiry');
  },

  // GET /api/medicines/expiring-in/{days}
  getExpiringInDays: async (days) => {
    return apiRequest(`/medicines/expiring-in/${days}`);
  },

  // GET /api/medicines/low-stock
  getLowStock: async () => {
    return apiRequest('/medicines/low-stock');
  },

  // GET /api/medicines/out-of-stock
  getOutOfStock: async () => {
    return apiRequest('/medicines/out-of-stock');
  },

  // GET /api/medicines/by-created-date?startDate=...&endDate=...
  getByCreatedDateRange: async (startDate, endDate) => {
    return apiRequest(`/medicines/by-created-date?startDate=${startDate}&endDate=${endDate}`);
  },

  // GET /api/medicines/by-expiry-date?startDate=...&endDate=...
  getByExpiryDateRange: async (startDate, endDate) => {
    return apiRequest(`/medicines/by-expiry-date?startDate=${startDate}&endDate=${endDate}`);
  },

  // POST /api/medicines?vendorId=...
  create: async (medicineData, vendorId) => {
    const url = vendorId ? `/medicines?vendorId=${vendorId}` : '/medicines';
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(medicineData),
    });
  },

  // PUT /api/medicines/{id}?vendorId=...
  // allowRepackChange: the user has confirmed they understand that changing the pack/dispense
  // sizes re-counts the stock already on hand. Without it the backend refuses such a change.
  update: async (id, medicineData, vendorId, allowRepackChange = false) => {
    const params = [];
    if (vendorId) params.push(`vendorId=${vendorId}`);
    if (allowRepackChange) params.push('allowRepackChange=true');
    const url = `/medicines/${id}${params.length ? `?${params.join('&')}` : ''}`;
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(medicineData),
    });
  },

  // PATCH /api/medicines/{id}/quantity
  updateQuantity: async (id, quantityChange, reason) => {
    return apiRequest(`/medicines/${id}/quantity`, {
      method: 'PATCH',
      body: JSON.stringify({ quantityChange, reason }),
    });
  },

  // PATCH /api/medicines/{id}/discontinued — mark discontinued (true) or restore (false)
  setDiscontinued: async (id, discontinued) =>
    apiRequest(`/medicines/${id}/discontinued`, {
      method: 'PATCH',
      body: JSON.stringify({ discontinued }),
    }),

  // GET /api/medicines/{id}/usage — reference counts across prescriptions, stock receipts,
  // purchase orders and the stock ledger, plus a `canDelete` flag. Checked before offering
  // to delete: a medicine that's already in use can only be discontinued.
  getUsage: async (id) => {
    return apiRequest(`/medicines/${id}/usage`);
  },

  // DELETE /api/medicines/{id}
  delete: async (id) => {
    return apiRequest(`/medicines/${id}`, {
      method: 'DELETE',
    });
  },

  // GET /api/medicines/stats
  getStats: async () => {
    return apiRequest('/medicines/stats');
  },

  // GET /api/medicines/types
  getTypes: async () => {
    return apiRequest('/medicines/types');
  },

  // GET /api/medicines/packaging-types
  getPackagingTypes: async () => {
    return apiRequest('/medicines/packaging-types');
  },
};

// =============================================
// ASSET API
// Backend: AssetController.java
// =============================================
export const assetAPI = {
  // GET /api/assets
  getAll: async () => {
    return apiRequest('/assets');
  },

  // GET /api/assets/{id}
  getById: async (id) => {
    return apiRequest(`/assets/${id}`);
  },

  // GET /api/assets/code/{assetCode}
  getByCode: async (assetCode) => {
    return apiRequest(`/assets/code/${assetCode}`);
  },

  // GET /api/assets/search?q={query}
  search: async (query) => {
    return apiRequest(`/assets/search?q=${encodeURIComponent(query)}`);
  },

  // GET /api/assets/status/{status}
  getByStatus: async (status) => {
    return apiRequest(`/assets/status/${status}`);
  },

  // GET /api/assets/category/{category}
  getByCategory: async (category) => {
    return apiRequest(`/assets/category/${category}`);
  },

  // GET /api/assets/condition/{condition}
  getByCondition: async (condition) => {
    return apiRequest(`/assets/condition/${condition}`);
  },

  // GET /api/assets/vendor/{vendorId}
  getByVendor: async (vendorId) => {
    return apiRequest(`/assets/vendor/${vendorId}`);
  },

  // GET /api/assets/location?location=...
  getByLocation: async (location) => {
    return apiRequest(`/assets/location?location=${encodeURIComponent(location)}`);
  },

  // GET /api/assets/department?department=...
  getByDepartment: async (department) => {
    return apiRequest(`/assets/department?department=${encodeURIComponent(department)}`);
  },

  // GET /api/assets/maintenance-due
  getMaintenanceDue: async () => {
    return apiRequest('/assets/maintenance-due');
  },

  // GET /api/assets/maintenance-upcoming
  getMaintenanceUpcoming: async () => {
    return apiRequest('/assets/maintenance-upcoming');
  },

  // GET /api/assets/under-maintenance
  getUnderMaintenance: async () => {
    return apiRequest('/assets/under-maintenance');
  },

  // GET /api/assets/warranty-expiring
  getWarrantyExpiring: async () => {
    return apiRequest('/assets/warranty-expiring');
  },

  // GET /api/assets/warranty-expired
  getWarrantyExpired: async () => {
    return apiRequest('/assets/warranty-expired');
  },

  // GET /api/assets/locations
  getLocations: async () => {
    return apiRequest('/assets/locations');
  },

  // GET /api/assets/departments
  getDepartments: async () => {
    return apiRequest('/assets/departments');
  },

  // POST /api/assets?vendorId=...
  create: async (assetData, vendorId, paymentProofFiles = []) =>
    withPaymentProof(
      vendorId ? `/assets?vendorId=${vendorId}` : '/assets', assetData, paymentProofFiles),

  // PUT /api/assets/{id}?vendorId=...
  update: async (id, assetData, vendorId) => {
    const url = vendorId ? `/assets/${id}?vendorId=${vendorId}` : `/assets/${id}`;
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(assetData),
    });
  },

  // PATCH /api/assets/{id}/status
  updateStatus: async (id, status) => {
    return apiRequest(`/assets/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  // POST /api/assets/{id}/maintenance
  recordMaintenance: async (id, notes) => {
    return apiRequest(`/assets/${id}/maintenance`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  // POST /api/assets/{id}/send-for-maintenance
  sendForMaintenance: async (id, reason) => {
    return apiRequest(`/assets/${id}/send-for-maintenance`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  // POST /api/assets/{id}/dispose
  dispose: async (id, reason) => {
    return apiRequest(`/assets/${id}/dispose`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  // DELETE /api/assets/{id}
  delete: async (id) => {
    return apiRequest(`/assets/${id}`, {
      method: 'DELETE',
    });
  },

  // GET /api/assets/stats
  getStats: async () => {
    return apiRequest('/assets/stats');
  },

  // GET /api/assets/categories
  getCategories: async () => {
    return apiRequest('/assets/categories');
  },

  // GET /api/assets/conditions
  getConditions: async () => {
    return apiRequest('/assets/conditions');
  },

  // GET /api/assets/statuses
  getStatuses: async () => {
    return apiRequest('/assets/statuses');
  },
};

// =============================================
// MEDICINE ORDER API
// Backend: MedicineOrderController.java
// =============================================
export const medicineOrderAPI = {
  // GET /api/medicine-orders
  getAll: async () => {
    return apiRequest('/medicine-orders');
  },

  // GET /api/medicine-orders/{id}
  getById: async (id) => {
    return apiRequest(`/medicine-orders/${id}`);
  },

  // GET /api/medicine-orders/number/{orderNumber}
  getByNumber: async (orderNumber) => {
    return apiRequest(`/medicine-orders/number/${orderNumber}`);
  },

  // GET /api/medicine-orders/active
  getActive: async () => {
    return apiRequest('/medicine-orders/active');
  },

  // POST /api/medicine-orders/active - Get or create active order
  getOrCreateActive: async () => {
    return apiRequest('/medicine-orders/active', { method: 'POST' });
  },

  // POST /api/medicine-orders/smart - Create smart order with low stock items
  createSmartOrder: async () => {
    return apiRequest('/medicine-orders/smart', { method: 'POST' });
  },

  // GET /api/medicine-orders/status/{status}
  getByStatus: async (status) => {
    return apiRequest(`/medicine-orders/status/${status}`);
  },

  // GET /api/medicine-orders/pending
  getPending: async () => {
    return apiRequest('/medicine-orders/pending');
  },

  // POST /api/medicine-orders/add-medicine
  addMedicine: async (medicineId, quantity = null) => {
    return apiRequest('/medicine-orders/add-medicine', {
      method: 'POST',
      body: JSON.stringify({ medicineId, quantity }),
    });
  },

  // POST /api/medicine-orders/add-medicines
  addMedicines: async (medicineIds) => {
    return apiRequest('/medicine-orders/add-medicines', {
      method: 'POST',
      body: JSON.stringify({ medicineIds }),
    });
  },

  // PATCH /api/medicine-orders/{orderId}/items/{itemId}/quantity
  updateItemQuantity: async (orderId, itemId, quantity) => {
    return apiRequest(`/medicine-orders/${orderId}/items/${itemId}/quantity`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity }),
    });
  },

  // DELETE /api/medicine-orders/{orderId}/items/{itemId}
  removeItem: async (orderId, itemId) => {
    return apiRequest(`/medicine-orders/${orderId}/items/${itemId}`, {
      method: 'DELETE',
    });
  },

  // PUT /api/medicine-orders/{id}
  update: async (id, data) => {
    return apiRequest(`/medicine-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // POST /api/medicine-orders/{id}/submit
  submit: async (id) => {
    return apiRequest(`/medicine-orders/${id}/submit`, { method: 'POST' });
  },

  // Orders are received in Stock Receipts, against the order; the old counter-bump receive is
  // retired (the server answers it with 410). The supplier is not sending the rest:
  // POST /api/medicine-orders/{id}/close-short
  closeShort: async (id, reason) => {
    return apiRequest(`/medicine-orders/${id}/close-short`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  // GET /api/medicine-orders/{id}/receipts - deliveries received against the order
  getReceipts: async (id) => {
    return apiRequest(`/medicine-orders/${id}/receipts`);
  },

  // POST /api/medicine-orders/{id}/cancel
  cancel: async (id, reason) => {
    return apiRequest(`/medicine-orders/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  // DELETE /api/medicine-orders/{id}
  delete: async (id) => {
    return apiRequest(`/medicine-orders/${id}`, { method: 'DELETE' });
  },

  // GET /api/medicine-orders/stats
  getStats: async () => {
    return apiRequest('/medicine-orders/stats');
  },

  // GET /api/medicine-orders/suggestions
  getSuggestions: async () => {
    return apiRequest('/medicine-orders/suggestions');
  },

  // GET /api/medicine-orders/statuses
  getStatuses: async () => {
    return apiRequest('/medicine-orders/statuses');
  },
};

// =============================================
// PRESCRIPTION API
// Backend: PrescriptionController.java
// =============================================
export const prescriptionAPI = {
  // POST /api/prescriptions/create/{visitId}?doctorId={doctorId}
  create: async (visitId, doctorId) => {
    return apiRequest(`/prescriptions/create/${visitId}?doctorId=${doctorId}`, {
      method: 'POST',
    });
  },

  // GET /api/prescriptions/patient/{patientId}/notes?type=examination|clinical
  // Used by the Patient Details "Examination Notes" + "Clinical Notes" tabs — returns the
  // chosen note field from every (non-template) prescription where it's non-blank, newest first.
  getPatientNotes: async (patientId, type = 'examination') =>
    apiRequest(`/prescriptions/patient/${patientId}/notes?type=${encodeURIComponent(type)}`),

  // GET /api/prescriptions/patient/{patientId}/summaries — Patient Details "Prescriptions" tab.
  getPatientSummaries: async (patientId) =>
    apiRequest(`/prescriptions/patient/${patientId}/summaries`),

  // GET /api/prescriptions/patient/{patientId}/medicines-history — Patient Details "Medicines" tab.
  getPatientMedicinesHistory: async (patientId) =>
    apiRequest(`/prescriptions/patient/${patientId}/medicines-history`),

  // GET /api/prescriptions/patient/{patientId}/lab-tests-history — Patient Details "Lab Tests" tab.
  getPatientLabTestsHistory: async (patientId) =>
    apiRequest(`/prescriptions/patient/${patientId}/lab-tests-history`),

  // GET /api/prescriptions/patient/{patientId}/alerts — Patient Details "Medical History" tab.
  getPatientAlerts: async (patientId) =>
    apiRequest(`/prescriptions/patient/${patientId}/alerts`),

  // GET /api/prescriptions/{id}
  getById: async (id) => {
    return apiRequest(`/prescriptions/${id}`);
  },

  // GET /api/prescriptions/number/{prescriptionNumber}
  getByNumber: async (prescriptionNumber) => {
    return apiRequest(`/prescriptions/number/${prescriptionNumber}`);
  },

  // GET /api/prescriptions/visit/{visitId}
  getByVisit: async (visitId) => {
    return apiRequest(`/prescriptions/visit/${visitId}`);
  },

  // PUT /api/prescriptions/{id}
  update: async (id, data) => {
    return apiRequest(`/prescriptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // POST /api/prescriptions/{id}/complete
  complete: async (id) => {
    return apiRequest(`/prescriptions/${id}/complete`, { method: 'POST' });
  },

  // DELETE /api/prescriptions/{id}/cancel
  cancel: async (id) => {
    return apiRequest(`/prescriptions/${id}/cancel`, { method: 'DELETE' });
  },

  // POST /api/prescriptions/{id}/copy-from/{sourceId}?replace=true|false
  // Fills this prescription from one of the patient's earlier ones, on the server in one
  // transaction, re-checking each medicine against today's stock. `replace` must be true when this
  // prescription already has medicines or lab tests (they are removed first); without it the server
  // answers 400 TARGET_NOT_EMPTY. Returns { prescription, copiedFrom: { id, number, date,
  // doctorName }, skipped: [{ kind, name, reason, message }], warnings: [{ kind, name, message }],
  // complaintDurationsCleared }.
  copyFrom: async (id, sourceId, replace = false) => {
    return apiRequest(`/prescriptions/${id}/copy-from/${sourceId}?replace=${replace ? 'true' : 'false'}`, {
      method: 'POST',
    });
  },

  // GET /api/prescriptions/{id}/private-notes?doctorId={doctorId}
  // The note is encrypted at rest and returned decrypted to anyone who can open the prescription.
  // There is no per-doctor gate: the note belongs to the prescription, not to its author.
  getPrivateNotes: async (prescriptionId) => {
    return apiRequest(`/prescriptions/${prescriptionId}/private-notes`);
  },

  // ============ MEDICINES ============

  // POST /api/prescriptions/{id}/medicines
  addMedicine: async (prescriptionId, medicineData) => {
    return apiRequest(`/prescriptions/${prescriptionId}/medicines`, {
      method: 'POST',
      body: JSON.stringify(medicineData),
    });
  },

  // DELETE /api/prescriptions/{id}/medicines/{medicineId}
  removeMedicine: async (prescriptionId, medicineId) => {
    return apiRequest(`/prescriptions/${prescriptionId}/medicines/${medicineId}`, {
      method: 'DELETE',
    });
  },

  // ============ LAB TESTS ============
  
  // POST /api/prescriptions/{id}/lab-tests
  addLabTest: async (prescriptionId, testData) => {
    return apiRequest(`/prescriptions/${prescriptionId}/lab-tests`, {
      method: 'POST',
      body: JSON.stringify(testData),
    });
  },

  // PUT /api/prescriptions/{id}/lab-tests/{labTestId} — update attached instructions (EN + Urdu)
  updateLabTest: async (prescriptionId, labTestId, data) => {
    return apiRequest(`/prescriptions/${prescriptionId}/lab-tests/${labTestId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // DELETE /api/prescriptions/{id}/lab-tests/{labTestId}
  removeLabTest: async (prescriptionId, labTestId) => {
    return apiRequest(`/prescriptions/${prescriptionId}/lab-tests/${labTestId}`, {
      method: 'DELETE',
    });
  },

  // GET /api/prescriptions/lab-tests/search?q={query}
  searchLabTests: async (query) => {
    return apiRequest(`/prescriptions/lab-tests/search?q=${encodeURIComponent(query)}`);
  },

  // GET /api/prescriptions/lab-tests/common
  getCommonLabTests: async () => {
    return apiRequest('/prescriptions/lab-tests/common');
  },

  // GET /api/prescriptions/lab-tests/quick — the quick-test chips for this doctor: their
  // speciality's configured list first, then the general one. Falls back to the common-test
  // catalog when nothing has been configured in Settings.
  getQuickLabTests: async (doctorId) => {
    return apiRequest(`/prescriptions/lab-tests/quick${doctorId ? `?doctorId=${doctorId}` : ''}`);
  },

  // ============ MEDICAL ALERTS ============
  // (getPatientAlerts is defined above alongside the patient-history helpers.)

  // POST /api/prescriptions/patient/{patientId}/alerts
  addMedicalAlert: async (patientId, alertData) => {
    return apiRequest(`/prescriptions/patient/${patientId}/alerts`, {
      method: 'POST',
      body: JSON.stringify(alertData),
    });
  },

  // PUT /api/prescriptions/patient/{patientId}/alerts/{alertId} — overwrite an alert
  updateMedicalAlert: async (patientId, alertId, alertData) => {
    return apiRequest(`/prescriptions/patient/${patientId}/alerts/${alertId}`, {
      method: 'PUT',
      body: JSON.stringify(alertData),
    });
  },

  // DELETE /api/prescriptions/patient/{patientId}/alerts/{alertId} — remove an alert
  deleteMedicalAlert: async (patientId, alertId) => {
    return apiRequest(`/prescriptions/patient/${patientId}/alerts/${alertId}`, {
      method: 'DELETE',
    });
  },

  // GET /api/prescriptions/patient/{patientId}/has-alerts
  patientHasAlerts: async (patientId) => {
    return apiRequest(`/prescriptions/patient/${patientId}/has-alerts`);
  },

  // ============ QUERIES ============
  
  // GET /api/prescriptions/patient/{patientId}
  getPatientPrescriptions: async (patientId) => {
    return apiRequest(`/prescriptions/patient/${patientId}`);
  },

  // GET /api/prescriptions/patient/{patientId}/last
  getLastPrescription: async (patientId) => {
    return apiRequest(`/prescriptions/patient/${patientId}/last`);
  },

  // GET /api/prescriptions/doctor/{doctorId}/today
  getDoctorTodayPrescriptions: async (doctorId) => {
    return apiRequest(`/prescriptions/doctor/${doctorId}/today`);
  },

  // GET /api/prescriptions/doctor/{doctorId}/all
  getDoctorAllPrescriptions: async (doctorId) => {
    return apiRequest(`/prescriptions/doctor/${doctorId}/all`);
  },

  // GET /api/prescriptions/all — all doctors (admin view)
  getAllPrescriptions: async () => {
    return apiRequest('/prescriptions/all');
  },

  // GET /api/prescriptions/today — all doctors, today only (admin view)
  getAllTodayPrescriptions: async () => {
    return apiRequest('/prescriptions/today');
  },

  // GET /api/prescriptions/range?from=YYYY-MM-DD&to=YYYY-MM-DD — inclusive date range
  getPrescriptionsByDateRange: async (from, to) => {
    return apiRequest(`/prescriptions/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  },

  // GET /api/prescriptions/pending-dispensing
  getPendingDispensing: async () => {
    return apiRequest('/prescriptions/pending-dispensing');
  },

  // ============ TEMPLATES ============
  
  // GET /api/prescriptions/templates/{doctorId}
  getTemplates: async (doctorId) => {
    return apiRequest(`/prescriptions/templates/${doctorId}`);
  },

  // All active templates — shared system-wide (any doctor/admin can use any template).
  getAllTemplates: async () => {
    return apiRequest('/prescriptions/templates');
  },

  // POST /api/prescriptions/templates
  saveTemplate: async (templateData) => {
    return apiRequest('/prescriptions/templates', {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
  },

  // DELETE /api/prescriptions/templates/{id}
  deleteTemplate: async (id) => {
    return apiRequest(`/prescriptions/templates/${id}`, {
      method: 'DELETE',
    });
  },

  // ============ CONFIGURATIONS ============
  
  // GET /api/prescriptions/config/{configType}
  // Pass doctorId to get the speciality-scoped list (that doctor's speciality rows first, then the
  // general ones). Omit it to get every active row regardless of speciality, which the print view
  // needs so it can resolve keys saved by any doctor.
  getConfig: async (configType, doctorId) => {
    return apiRequest(`/prescriptions/config/${configType}${doctorId ? `?doctorId=${doctorId}` : ''}`);
  },

  // GET /api/prescriptions/followup-urdu/{days}
  getFollowupUrdu: async (days) => {
    return apiRequest(`/prescriptions/followup-urdu/${days}`);
  },

  // ============ ENUMS ============
  
  // GET /api/prescriptions/enums/frequencies
  getFrequencies: async () => {
    return apiRequest('/prescriptions/enums/frequencies');
  },

  // GET /api/prescriptions/enums/timings
  getTimings: async () => {
    return apiRequest('/prescriptions/enums/timings');
  },

  // GET /api/prescriptions/enums/duration-units
  getDurationUnits: async () => {
    return apiRequest('/prescriptions/enums/duration-units');
  },

  // GET /api/prescriptions/enums/alert-types
  getAlertTypes: async () => {
    return apiRequest('/prescriptions/enums/alert-types');
  },

  // GET /api/prescriptions/enums/severities
  getSeverities: async () => {
    return apiRequest('/prescriptions/enums/severities');
  },

  // GET /api/prescriptions/enums/statuses
  getStatuses: async () => {
    return apiRequest('/prescriptions/enums/statuses');
  },
};

// =============================================
// ROLE PERMISSION API
// Backend: RolePermissionController.java
// =============================================
export const rolePermissionAPI = {
  // GET /api/roles/permissions
  getAll: async () => {
    return apiRequest('/roles/permissions');
  },

  // GET /api/roles/{role}/permissions
  getByRole: async (role) => {
    return apiRequest(`/roles/${role}/permissions`);
  },

  // PUT /api/roles/{role}/permissions
  updateRole: async (role, permissions) => {
    return apiRequest(`/roles/${role}/permissions`, {
      method: 'PUT',
      body: JSON.stringify(permissions),
    });
  },

  // PATCH /api/roles/{role}/permissions/{module}/{action}
  togglePermission: async (role, module, action, granted) => {
    return apiRequest(`/roles/${role}/permissions/${module}/${action}`, {
      method: 'PATCH',
      body: JSON.stringify({ granted }),
    });
  },

  // POST /api/roles/{role}/permissions/reset
  resetToDefaults: async (role) => {
    return apiRequest(`/roles/${role}/permissions/reset`, {
      method: 'POST',
    });
  },

  // GET /api/roles/summary
  getSummary: async () => {
    return apiRequest('/roles/summary');
  },

  // GET /api/roles/{role}/users — who actually holds a role. Fetched on demand from the role
  // cards rather than bundled into the summary, which every visit to the screen loads.
  getUsersForRole: async (role) => {
    return apiRequest(`/roles/${encodeURIComponent(role)}/users`);
  },

  // GET /api/roles/permissions/catalog — per-module list of valid actions. Drives the toggles
  // shown in the permissions UI: a module here with extra entries beyond V/C/E/D (currently
  // only PATIENTS) renders an expanded action set.
  getCatalog: async () => {
    return apiRequest('/roles/permissions/catalog');
  },

  // GET /api/roles — list all roles
  getRoles: async () => {
    return apiRequest('/roles');
  },

  // POST /api/roles — create a new role
  createRole: async (data) => {
    return apiRequest('/roles', { method: 'POST', body: JSON.stringify(data) });
  },

  // PUT /api/roles/{name} — update role metadata
  updateRoleMeta: async (name, data) => {
    return apiRequest(`/roles/${name}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  // DELETE /api/roles/{name} — delete custom role
  deleteRole: async (name) => {
    return apiRequest(`/roles/${name}`, { method: 'DELETE' });
  },
};

// =============================================
// AUTH API
// Backend: AuthController.java
// =============================================
export const authAPI = {
  // POST /api/auth/login
  login: async (username, password) => {
    const url = `${API_BASE_URL}/auth/login`;
    // With the server down this routes to the outage page rather than a failed sign-in.
    const response = await serverFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json().catch(() => ({ error: 'Network error' }));

    if (!response.ok) {
      throw new Error(data.error || `Login failed (${response.status})`);
    }

    return data;
  },

  // POST /api/auth/change-password
  changePassword: async (username, currentPassword, newPassword) => {
    const url = `${API_BASE_URL}/auth/change-password`;
    const response = await serverFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, currentPassword, newPassword }),
    });

    const data = await response.json().catch(() => ({ error: 'Network error' }));

    if (!response.ok) {
      throw new Error(data.error || `Password change failed (${response.status})`);
    }

    return data;
  },
};

// =============================================
// INVOICE API
// Backend: InvoiceController.java
// =============================================
export const invoiceAPI = {
  // GET /api/invoices?from=&to= — the All invoices list: one light row per bill completed in the
  // range (YYYY-MM-DD, both days included, either may be left out), or every bill with neither.
  // A row is not the whole bill: open it with getById.
  getAll: async ({ from, to } = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const s = qs.toString();
    return apiRequest(`/invoices${s ? `?${s}` : ''}`);
  },

  // GET /api/invoices/today
  getToday: async () => {
    return apiRequest('/invoices/today');
  },

  // GET /api/invoices/search?q=
  search: async (query) => {
    return apiRequest(`/invoices/search?q=${encodeURIComponent(query || '')}`);
  },

  // GET /api/invoices/{id}
  getById: async (id) => {
    return apiRequest(`/invoices/${id}`);
  },

  // GET /api/invoices/patient/{patientId}
  getByPatient: async (patientId) => {
    return apiRequest(`/invoices/patient/${patientId}`);
  },

  // GET /api/invoices/prescription/{prescriptionId}
  getByPrescription: async (prescriptionId) => {
    return apiRequest(`/invoices/prescription/${prescriptionId}`);
  },

  // POST /api/invoices/initiate/{prescriptionId} — create invoice from PRESCRIBED Rx
  initiate: async (prescriptionId) => {
    return apiRequest(`/invoices/initiate/${prescriptionId}`, { method: 'POST' });
  },

  // GET /api/invoices/counter?from=&to= or ?all=true: the billing counter's list. Prescriptions
  // waiting to be billed in the range (with all, whatever their date), and every bill started but
  // not paid. The dates default to today.
  counter: async ({ from, to, all } = {}) => {
    const qs = new URLSearchParams();
    if (all) {
      qs.set('all', 'true');
    } else {
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
    }
    const s = qs.toString();
    return apiRequest(`/invoices/counter${s ? `?${s}` : ''}`);
  },

  // GET /api/invoices/preview/{prescriptionId} — the bill a prescription would make, NOT saved:
  // { invoice, shortfalls, ok }. Looking at a patient locks nothing.
  preview: async (prescriptionId) => apiRequest(`/invoices/preview/${prescriptionId}`),

  // POST /api/invoices/checkout/{prescriptionId} — create the bill and take its payment in one
  // transaction. Same multipart shape as finalize-and-pay, so a transfer screenshot travels with it.
  // Body: { expectedTotal, paymentMode, paymentReference, cashTendered }.
  checkout: async (prescriptionId, payment, paymentProofFiles = []) =>
    withPaymentProof(`/invoices/checkout/${prescriptionId}`, payment, paymentProofFiles),

  // A treatment course's visit: the patient is back for the next dose of an injection or drip
  // given one dose per visit. The same three steps as a prescription's bill, keyed on the course.
  coursePreview: async (courseId) => apiRequest(`/invoices/course/${courseId}/preview`),
  courseInitiate: async (courseId) =>
    apiRequest(`/invoices/course/${courseId}/initiate`, { method: 'POST' }),
  courseCheckout: async (courseId, payment, paymentProofFiles = []) =>
    withPaymentProof(`/invoices/course/${courseId}/checkout`, payment, paymentProofFiles),

  // POST /api/invoices/{id}/extra-service/{serviceId}?quantity=N
  addExtraService: async (invoiceId, serviceId, quantity = 1) => {
    return apiRequest(`/invoices/${invoiceId}/extra-service/${serviceId}?quantity=${quantity}`, {
      method: 'POST',
    });
  },

  // GET /api/invoices/supply-options?prescriptionId= — the supplies a bill can offer. Keyed on the
  // prescription because the counter works on an unsaved preview, which has no invoice id yet.
  supplyOptions: async (prescriptionId) =>
    apiRequest(`/invoices/supply-options?prescriptionId=${prescriptionId}`),

  // POST /api/invoices/{id}/supply/{medicineId}?quantity=N — put a stocked supply on the bill.
  addSupply: async (invoiceId, medicineId, quantity = 1) =>
    apiRequest(`/invoices/${invoiceId}/supply/${medicineId}?quantity=${quantity}`, { method: 'POST' }),

  // POST /api/invoices/{id}/custom-item
  addCustomItem: async (invoiceId, item) => {
    return apiRequest(`/invoices/${invoiceId}/custom-item`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  },

  // DELETE /api/invoices/{id}/items/{itemId}
  removeItem: async (invoiceId, itemId) => {
    return apiRequest(`/invoices/${invoiceId}/items/${itemId}`, { method: 'DELETE' });
  },

  // PATCH /api/invoices/{id}/items/{itemId}/quantity?quantity=N
  updateItemQuantity: async (invoiceId, itemId, quantity) => {
    return apiRequest(`/invoices/${invoiceId}/items/${itemId}/quantity?quantity=${quantity}`, {
      method: 'PATCH',
    });
  },

  // PATCH /api/invoices/{id}/items/{itemId}/discount?amount=N
  // Switch the whole invoice between % and Rs discounts. The server converts the lines already
  // entered, so flipping the toggle to look at the other unit never wipes what is there.
  updateDiscountMode: async (invoiceId, mode) => {
    return apiRequest(`/invoices/${invoiceId}/discount-mode?mode=${encodeURIComponent(mode)}`, {
      method: 'PATCH',
    });
  },

  // The figure's unit comes from the invoice's discount mode, not from this call.
  updateItemDiscount: async (invoiceId, itemId, amount) => {
    return apiRequest(`/invoices/${invoiceId}/items/${itemId}/discount?amount=${amount}`, {
      method: 'PATCH',
    });
  },

  // PATCH /api/invoices/{id}/discount?amount=N
  applyDiscount: async (invoiceId, amount) => {
    return apiRequest(`/invoices/${invoiceId}/discount?amount=${amount}`, { method: 'PATCH' });
  },

  // POST /api/invoices/{id}/finalize
  finalize: async (invoiceId) => {
    return apiRequest(`/invoices/${invoiceId}/finalize`, { method: 'POST' });
  },

  // GET /api/invoices/{id}/stock-check — what this draft cannot dispense, if anything.
  // Read-only, so the screen can warn while the bill is still editable rather than failing at
  // Confirm after a payment method has been chosen.
  stockCheck: async (invoiceId) => apiRequest(`/invoices/${invoiceId}/stock-check`),

  // POST /api/invoices/{id}/finalize-and-pay — the whole transition in one call. A draft goes
  // straight to PAID; there is no state in between any more. Same multipart shape as the payment
  // endpoint, so the transfer screenshot travels with it.
  finalizeAndPay: async (invoiceId, paymentData, paymentProofFiles = []) =>
    withPaymentProof(`/invoices/${invoiceId}/finalize-and-pay`, paymentData, paymentProofFiles),

  // POST /api/invoices/{id}/payment
  // RETIRED with the FINALIZED state. Nothing reaches it now; kept so an invoice that somehow
  // still sits in FINALIZED could be settled.
  recordPayment: async (invoiceId, paymentData, paymentProofFiles = []) =>
    withPaymentProof(`/invoices/${invoiceId}/payment`, paymentData, paymentProofFiles),

  // POST /api/invoices/{id}/cancel — reason is required server-side
  cancel: async (invoiceId, reason) => {
    return apiRequest(`/invoices/${invoiceId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// ==================== PATIENT DOCUMENTS ====================
// ==================== BRANDING (clinic logo) ====================
export const brandingAPI = {
  // GET /api/branding — { hasCustomLogo, logoVersion, profile } (public endpoint).
  // `profile` carries the clinic's printed identity: nameEnglish, nameUrdu, address, phone,
  // timings. It rides on this call rather than one of its own because every consumer of the
  // letterhead (Rx print, login, display board) already makes it.
  get: async () => apiRequest('/branding'),

  // PUT /api/branding/profile — save the clinic's printed identity (auth required)
  updateProfile: async (profile) =>
    apiRequest('/branding/profile', { method: 'PUT', body: JSON.stringify(profile) }),

  // Versioned public logo URL — safe to long-cache because the version bumps on every upload.
  logoUrl: (version = 0) => `${API_BASE_URL}/branding/logo?v=${version}`,

  // POST /api/branding/logo — multipart upload (PNG/JPEG/SVG/WebP, max 2MB)
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('clinic_auth_token');
    const response = await serverFetch(`${API_BASE_URL}/branding/logo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Failed to upload logo');
    return data;
  },

  // DELETE /api/branding/logo — revert to the built-in logo (uploads stay in history)
  removeLogo: async () => apiRequest('/branding/logo', { method: 'DELETE' }),

  // GET /api/branding/history — every uploaded logo version, newest first
  getHistory: async () => apiRequest('/branding/history'),

  // Image of a specific stored version (auth required — wrap in authedMediaUrl for <img> tags)
  historyImageUrl: (id) => `${API_BASE_URL}/branding/history/${id}/image`,

  // POST /api/branding/history/{id}/activate — make a previous version the live logo again
  activateVersion: async (id) => apiRequest(`/branding/history/${id}/activate`, { method: 'POST' }),
};

// ==================== IN-CLINIC REFERRALS + PATIENT CREDIT ====================
// Injections and drips given here one dose per visit: the Nurse Station's Injections tab.
export const treatmentCourseAPI = {
  // GET /api/treatment-courses/today — { toGive, givenToday, behind }
  today: async () => apiRequest('/treatment-courses/today'),

  // POST /api/treatment-courses/doses/given — record doses as given by whoever is signed in
  markGiven: async (doseIds) =>
    apiRequest('/treatment-courses/doses/given', {
      method: 'POST',
      body: JSON.stringify({ doseIds }),
    }),

  // POST /api/treatment-courses/{id}/stop — end a course early, with a reason
  stop: async (courseId, reason) =>
    apiRequest(`/treatment-courses/${courseId}/stop`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

export const referralAPI = {
  // POST /api/referrals/{visitId}/refer — doctor refers the patient to a colleague
  refer: async (visitId, toDoctorId, reason) =>
    apiRequest(`/referrals/${visitId}/refer`, {
      method: 'POST',
      body: JSON.stringify({ toDoctorId, reason }),
    }),

  // POST /api/referrals/{visitId}/settle — reception settles the paid fee.
  // outcome: TOKEN_NOW | CREDIT | REFUND | NONE; TOKEN_NOW also takes medicalServiceId,
  // paymentMode (top-up) and excessAction (CASH | CREDIT).
  settle: async (visitId, payload) =>
    apiRequest(`/referrals/${visitId}/settle`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // GET /api/referrals/credit/{patientId} — { balance, entries[] }
  getCredit: async (patientId) => apiRequest(`/referrals/credit/${patientId}`),
};

// ==================== CASH DRAWERS ====================
// Backend: CashDrawerController.java. A cashier opens, counts and closes their own drawer; whoever
// holds the Cash Drawers permissions sees every drawer. Refusals come back as 409 with a code the
// screens act on: DRAWER_NOT_OPEN, DRAWER_ALREADY_OPEN, COUNT_ENTERED, DRAWER_CHANGED,
// REASON_REQUIRED, DRAWER_CLOSED.
export const cashDrawerAPI = {
  // GET /api/cash-drawers/mine — { drawer } with no expected figure until the count is entered.
  mine: async () => apiRequest('/cash-drawers/mine'),

  // POST /api/cash-drawers/open — { openingFloat }
  open: async (openingFloat) =>
    apiRequest('/cash-drawers/open', { method: 'POST', body: JSON.stringify({ openingFloat }) }),

  // POST /api/cash-drawers/{id}/count — the blind count. { stage: 'CLOSED' } when it matched,
  // { stage: 'COUNTED' } with the difference when it did not. Both carry drawer, summary, movements.
  count: async (id, countedCash) =>
    apiRequest(`/cash-drawers/${id}/count`, { method: 'POST', body: JSON.stringify({ countedCash }) }),

  // POST /api/cash-drawers/{id}/close — { reason } for a counted drawer that did not match.
  close: async (id, reason) =>
    apiRequest(`/cash-drawers/${id}/close`, { method: 'POST', body: JSON.stringify({ reason }) }),

  // GET /api/cash-drawers?from=YYYY-MM-DD&to=YYYY-MM-DD — drawers opened between the dates, plus
  // every drawer still open.
  list: async ({ from, to }) =>
    apiRequest(`/cash-drawers?${new URLSearchParams({ from, to }).toString()}`),

  // GET /api/cash-drawers/{id} — { stage, drawer, summary, movements }
  getById: async (id) => apiRequest(`/cash-drawers/${id}`),
};

export const patientDocumentAPI = {
  getByPatient: async (patientId, type = null) => {
    const params = type ? `?type=${type}` : '';
    return apiRequest(`/patients/${patientId}/documents${params}`);
  },

  // `linkedLabTestIds` is an optional array of PrescriptionLabTest ids — used by the Lab
  // Report upload flow to attach this file to the ordered tests it covers and auto-mark
  // each one completed. Ignored for non-Lab-Report uploads. `customFileName` (optional)
  // replaces the original filename as the document's display name.
  upload: async (patientId, file, type, description = '', documentDate = '', linkedLabTestIds = [], customFileName = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (description) formData.append('description', description);
    if (documentDate) formData.append('documentDate', documentDate); // ISO yyyy-MM-dd
    if (Array.isArray(linkedLabTestIds)) {
      // Repeated form field with the same name = Spring binds it as List<UUID>.
      linkedLabTestIds.forEach(id => formData.append('linkedLabTestIds', id));
    }
    if (customFileName && customFileName.trim()) formData.append('customFileName', customFileName.trim());

    const token = localStorage.getItem('clinic_auth_token');
    const response = await serverFetch(`${API_BASE_URL}/patients/${patientId}/documents`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(err.message || 'Upload failed');
    }
    return response.json();
  },

  getDownloadUrl: (patientId, docId) =>
    `${API_BASE_URL}/patients/${patientId}/documents/${docId}/download`,

  getThumbnailUrl: (patientId, docId) =>
    `${API_BASE_URL}/patients/${patientId}/documents/${docId}/thumbnail`,

  delete: async (patientId, docId) =>
    apiRequest(`/patients/${patientId}/documents/${docId}`, { method: 'DELETE' }),
};

// Admin-only: review queue + disk cleanup for soft-deleted patient documents.
export const deletedDocumentAPI = {
  // filter: 'pending' (default) | 'kept' | 'all'
  getDeletions: async (filter = 'pending') => apiRequest(`/admin/deleted-documents?filter=${filter}`),
  getStats: async () => apiRequest('/admin/deleted-documents/stats'),
  keep: async (docId) => apiRequest(`/admin/deleted-documents/${docId}/keep`, { method: 'POST' }),
  restore: async (docId) => apiRequest(`/admin/deleted-documents/${docId}/restore`, { method: 'POST' }),
  purge: async (docId) => apiRequest(`/admin/deleted-documents/${docId}`, { method: 'DELETE' }),
  cleanup: async (filter = 'pending') => apiRequest(`/admin/deleted-documents/cleanup?filter=${filter}`, { method: 'POST' }),
};

// Admin-only: restore view for soft-deleted (archived) patients.
export const deletedPatientAPI = {
  list: async () => apiRequest('/admin/deleted-patients'),
  restore: async (id) => apiRequest(`/admin/deleted-patients/${id}/restore`, { method: 'POST' }),
  purge: async (id) => apiRequest(`/admin/deleted-patients/${id}`, { method: 'DELETE' }),
};

// Export all APIs
// ==================== EXPENSES ====================
// Backend: ExpenseController.java. Files travel as multipart beside a JSON `payload` part:
// `paymentProofs` (the transfer screenshot a non-cash payment must carry) and `attachments` (bill,
// receipt and cheque photos). Without files the same call is plain JSON.
const expenseMultipart = async (path, payload, { paymentProofs = [], attachments = [] } = {}, method = 'POST') => {
  if (paymentProofs.length === 0 && attachments.length === 0) {
    return apiRequest(path, { method, body: JSON.stringify(payload) });
  }
  const token = localStorage.getItem('clinic_auth_token');
  const formData = new FormData();
  formData.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  paymentProofs.forEach(f => formData.append('paymentProofs', f));
  attachments.forEach(f => formData.append('attachments', f));
  const response = await serverFetch(`${API_BASE_URL}${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401 || response.status === 403) {
    if (!signOutUnlessTokenReplaced(token)) throw new Error('Please try again.');
    throw new Error('Session expired. Please log in again.');
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.message || data.error || 'Request failed');
    err.code = data.code || data.error;
    err.status = response.status;
    err.body = data;
    err.data = data;
    throw err;
  }
  return data;
};

const expenseQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) params.append(k, v);
  });
  const q = params.toString();
  return q ? `?${q}` : '';
};

export const expenseAPI = {
  // Figures. `categoryId` scopes to a department (a root and its categories) or one category.
  getDashboard: async (from, to, categoryId) =>
    apiRequest(`/expenses/dashboard${expenseQuery({ from, to, categoryId })}`),
  getCashPosition: async (categoryId) =>
    apiRequest(`/expenses/cash-position${expenseQuery({ categoryId })}`),
  // GET /api/expenses/monthly-report: flat month x department rows for the Monthly Report tab.
  getMonthlyReport: async (from, to) =>
    apiRequest(`/expenses/monthly-report${expenseQuery({ from, to })}`),
  getTopExpenses: async (from, to, limit = 25) =>
    apiRequest(`/expenses/top${expenseQuery({ from, to, limit })}`),
  // The banks seen so far and the one each department last drew from.
  getMeta: async () => apiRequest('/expenses/meta'),

  // Entries.
  getEntries: async (filters = {}) => apiRequest(`/expenses/entries${expenseQuery(filters)}`),
  getEntry: async (id) => apiRequest(`/expenses/entries/${id}`),
  createEntry: async (data, files = {}) => expenseMultipart('/expenses/entries', data, files),
  updateEntry: async (id, data, files = {}) =>
    expenseMultipart(`/expenses/entries/${id}`, data, files, 'PUT'),
  deleteEntry: async (id) => apiRequest(`/expenses/entries/${id}`, { method: 'DELETE' }),
  // Several at once (a copied month), all or none; lines already in the ledger are skipped.
  createBatch: async (items) =>
    apiRequest('/expenses/entries/batch', { method: 'POST', body: JSON.stringify({ items }) }),
  getSuggestions: async (categoryId) =>
    apiRequest(`/expenses/entries/suggestions${expenseQuery({ categoryId })}`),
  getMonths: async (categoryId) =>
    apiRequest(`/expenses/entries/months${expenseQuery({ categoryId })}`),

  // Attachments.
  attachmentFileUrl: (id) => `${API_BASE_URL}/expenses/attachments/${id}/file`,
  addAttachments: async (entryId, files) => {
    const token = localStorage.getItem('clinic_auth_token');
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await serverFetch(`${API_BASE_URL}/expenses/entries/${entryId}/attachments`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || data.error || 'Upload failed');
    return data;
  },
  deleteAttachment: async (id) => apiRequest(`/expenses/attachments/${id}`, { method: 'DELETE' }),

  // Departments and their categories.
  getCategories: async (includeInactive = false) =>
    apiRequest(`/expenses/categories${expenseQuery({ includeInactive })}`),
  getCategoriesFlat: async () => apiRequest('/expenses/categories/flat'),
  getSubCategories: async (parentId) => apiRequest(`/expenses/categories/${parentId}/children`),
  createCategory: async (data) => apiRequest('/expenses/categories', { method: 'POST', body: JSON.stringify(data) }),
  updateCategory: async (id, data) => apiRequest(`/expenses/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCategory: async (id) => apiRequest(`/expenses/categories/${id}`, { method: 'DELETE' }),

  // Cheques.
  getCheques: async () => apiRequest('/expenses/cheques'),
  lookupCheque: async (number, bank) =>
    apiRequest(`/expenses/cheques/lookup${expenseQuery({ number, bank })}`),
  // Every entry on one cheque; encoded because a cheque number is typed by hand.
  getChequeEntries: async (num, bank) =>
    apiRequest(`/expenses/cheques/${encodeURIComponent(num)}${expenseQuery({ bank })}`),

  // Notes.
  getNotes: async () => apiRequest('/expenses/notes'),
  createNote: async (body) => apiRequest('/expenses/notes', { method: 'POST', body: JSON.stringify({ body }) }),
  updateNote: async (id, body) => apiRequest(`/expenses/notes/${id}`, { method: 'PUT', body: JSON.stringify({ body }) }),
  deleteNote: async (id) => apiRequest(`/expenses/notes/${id}`, { method: 'DELETE' }),

  createReceipt: async (data) => apiRequest('/expenses/receipts', { method: 'POST', body: JSON.stringify(data) }),
};

// Stock receipts — inflow side of inventory. Receipt = supplier invoice header; lines = the
// medicines delivered with batch/expiry/qty/price. Saving bumps medicine.quantity by line qty.
export const stockReceiptAPI = {
  // Absolute path to a bill copy, for authedMediaUrl. It has to carry the API base: a browser
  // navigating to a bare /stock-receipts/... path is served the single-page app by nginx, so the
  // bill link silently opened the dashboard instead of the image.
  billFileUrl: (billId) => `${API_BASE_URL}/stock-receipts/bills/${billId}/file`,

  list: async ({ vendorId, from, to } = {}) => {
    const params = new URLSearchParams();
    if (vendorId) params.append('vendorId', vendorId);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    return apiRequest(`/stock-receipts${qs ? '?' + qs : ''}`);
  },
  getById: async (id) => apiRequest(`/stock-receipts/${id}`),
  // Receipts that contain a specific medicine — used by the Medicines page's "Update Stock"
  // picker. Empty list means the receptionist needs to add a Stock Receipt first.
  listByMedicine: async (medicineId) => apiRequest(`/stock-receipts/by-medicine/${medicineId}`),
  // Multipart, not JSON: a receipt must arrive with the supplier's bill copy attached, and sending
  // the record and its proof in one request is what makes that atomic. `payload` is the same object
  // the JSON body used to carry; `bills` are the newly picked files.
  create: async (data, billFiles = [], paymentProofFiles = []) =>
    receiptMultipart(`${API_BASE_URL}/stock-receipts`, 'POST', data, billFiles, paymentProofFiles),
  update: async (id, data, billFiles = [], paymentProofFiles = []) =>
    receiptMultipart(`${API_BASE_URL}/stock-receipts/${id}`, 'PUT', data, billFiles, paymentProofFiles),
  cancel: async (id) => apiRequest(`/stock-receipts/${id}/cancel`, { method: 'POST' }),
  historyForMedicine: async (medicineId) =>
    apiRequest(`/stock-receipts/history/medicine/${medicineId}`),
  // Sent orders a delivery from this supplier can be received against, with what each line still
  // expects. Backs "Against order" on the receipt form.
  openOrders: async (vendorId) =>
    apiRequest(`/stock-receipts/open-orders${vendorId ? `?vendorId=${vendorId}` : ''}`),
};

// =============================================
// PLANNER API (the Order Board)
// Backend: PlannerController.java
// =============================================
export const plannerAPI = {
  // Every item the shelf needs, grouped by the supplier to order it from, with a watch list.
  // days: plan every order to last that long after its delivery (7 to 120); none for the planner's own.
  getBoard: async (days) => apiRequest(`/planner/board${days ? `?days=${days}` : ''}`),
  // Draft orders, one per supplier.
  getDrafts: async () => apiRequest('/planner/drafts'),
  // Puts lines ({medicineId, packs}) on the supplier's draft, creating it if there is none.
  addToDraft: async (vendorId, lines) =>
    apiRequest('/planner/drafts', { method: 'POST', body: JSON.stringify({ vendorId, lines }) }),
  setPacks: async (orderId, itemId, packs) =>
    apiRequest(`/planner/drafts/${orderId}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify({ packs }) }),
  // The supplier's draft becomes exactly these lines ({medicineId, packs}); none discards it.
  // days: the period the board was planned for, so each line is planned the same.
  replaceDraft: async (vendorId, lines, days) =>
    apiRequest(`/planner/drafts/vendor/${vendorId}${days ? `?days=${days}` : ''}`, { method: 'PUT', body: JSON.stringify(lines) }),
  // Adds lines ({medicineId, packs}) to a supplier's order out for review, their one order, instead
  // of starting a second. Answers { orderNumber, added, raised, alreadyThere } (item names).
  addToOrderInReview: async (orderId, lines, days) =>
    apiRequest(`/planner/in-review/${orderId}/lines${days ? `?days=${days}` : ''}`, { method: 'POST', body: JSON.stringify(lines) }),
  // Leave a line off, for a reason the planner acts on: NOT_NEEDED, ELSEWHERE, OVERSTOCKED, STOPPED.
  skip: async (medicineId, vendorId, reason) =>
    apiRequest('/planner/skip', { method: 'POST', body: JSON.stringify({ medicineId, vendorId, reason }) }),
  // Who takes orders today, and how many items run out before any delivery: reminder + menu count.
  getToday: async () => apiRequest('/planner/today'),
  // This month and last: stock-out days, buying without an order, hand changes, expired value.
  getMeasures: async () => apiRequest('/planner/measures'),
  getDefaults: async () => apiRequest('/planner/defaults'),
  saveDefaults: async (data) => apiRequest('/planner/defaults', { method: 'PUT', body: JSON.stringify(data) }),
};

// =============================================
// ORDER REVIEWS API
// Backend: OrderReviewController.java
// =============================================
// An order checked by a colleague before it goes to the supplier. The reviewer needs no permission
// on Medicine Orders: the order shared with them is the permission, and the server decides.
export const orderReviewAPI = {
  // { toReview, backToMe, waiting, finished }: the caller's own reviews.
  mine: async () => apiRequest('/order-reviews'),
  // { toReview, toDecide, waiting, involved } for the menu. Answers anybody signed in.
  getStats: async () => apiRequest('/order-reviews/stats'),
  // { id, fullName, role } of everybody active but the caller.
  reviewers: async () => apiRequest('/order-reviews/reviewers'),
  get: async (orderId) => apiRequest(`/order-reviews/${orderId}`),
  share: async (orderId, reviewerId, note) =>
    apiRequest(`/order-reviews/${orderId}/share`, { method: 'POST', body: JSON.stringify({ reviewerId, note }) }),
  // lines: [{ lineId, medicineId, packs, reason }]; a line with no lineId is one the reviewer added.
  save: async (orderId, lines, note) =>
    apiRequest(`/order-reviews/${orderId}/lines`, { method: 'PUT', body: JSON.stringify({ lines, note }) }),
  sendBack: async (orderId, lines, note) =>
    apiRequest(`/order-reviews/${orderId}/send-back`, { method: 'POST', body: JSON.stringify({ lines, note }) }),
  takeBack: async (orderId) => apiRequest(`/order-reviews/${orderId}/take-back`, { method: 'POST' }),
  // decisions: { lineId: 'TAKEN' | 'KEPT' }; then: 'SEND' (answers with the sent order) or 'SHARE'.
  accept: async (orderId, body) =>
    apiRequest(`/order-reviews/${orderId}/accept`, { method: 'POST', body: JSON.stringify(body) }),
  discard: async (orderId, reason) =>
    apiRequest(`/order-reviews/${orderId}/discard`, { method: 'POST', body: JSON.stringify({ reason }) }),
  catalogue: async (orderId) => apiRequest(`/order-reviews/${orderId}/catalogue`),
  preview: async (orderId, medicineId) => apiRequest(`/order-reviews/${orderId}/preview/${medicineId}`),
  // Who else an item could be ordered from: names and last delivery, never a price.
  suppliers: async (orderId, medicineId) => apiRequest(`/order-reviews/${orderId}/suppliers/${medicineId}`),
};

// =============================================
// REORDER API (purchase planner inputs)
// Backend: ReorderController.java
// =============================================
export const reorderAPI = {
  getSettings: async () => apiRequest('/reorder/settings'),
  saveSettings: async (medicineId, data) =>
    apiRequest(`/reorder/settings/${medicineId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getDemandEvents: async ({ from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    return apiRequest(`/reorder/demand-events${qs ? '?' + qs : ''}`);
  },
  // The writer refused a medicine as out of stock. Fire and forget: the record must never get in
  // the way of the doctor, so a failure is swallowed.
  noteRefused: (medicineId, prescriptionId, reason) =>
    apiRequest('/reorder/demand-events/refused', {
      method: 'POST',
      body: JSON.stringify({ medicineId, prescriptionId, reason }),
    }).catch(() => {}),
  getVendorItemNames: async (vendorId) => apiRequest(`/reorder/vendor-item-names/${vendorId}`),
};

// =============================================
// REPORTS API
// Backend: ReportController.java
// =============================================
// One GET per report; every method takes a filters object and forwards only the set values,
// so the backend's defaults (usually last-30-days) apply when a filter is blank.
const reportRequest = (path, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, v);
  });
  const qs = params.toString();
  return apiRequest(`/reports/${path}${qs ? `?${qs}` : ''}`);
};

export const reportsAPI = {
  // Finance
  getDailyCollection: async (date) => reportRequest('daily-collection', { date }),
  getRevenueTrend: async (filters) => reportRequest('revenue-trend', filters),
  // Month by month spending and care, and the next three months if things carry on: { months }.
  getFinancialOutlook: async (filters) => reportRequest('financial-outlook', filters),
  // Supplies put on a bill at the counter that were never on the prescription.
  getSuppliesGiven: async (filters) => reportRequest('supplies-given', filters),
  getRevenueByService: async (filters) => reportRequest('revenue-by-service', filters),
  getReceivables: async () => reportRequest('receivables'),
  getTopPayers: async (filters) => reportRequest('top-payers', filters),
  // Dated by the day a bill was raised, not the day it was completed: the question is what became
  // of the bills started in the period.
  getNotCompletedSameDay: async (filters) => reportRequest('not-completed-same-day', filters),
  // Work started at either end of a visit and left: tokens nobody got to, prescriptions
  // the doctor opened and never finished.
  getUnfinishedVisits: async (filters) => reportRequest('unfinished-visits', filters),
  // OPD
  getVisitTrends: async (filters) => reportRequest('visit-trends', filters),
  getDoctorLoad: async (filters) => reportRequest('doctor-load', filters),
  getTokenEdits: async (filters) => reportRequest('token-edits', filters),
  getVitalsEdits: async (filters) => reportRequest('vitals-edits', filters),
  getNewVsReturning: async (filters) => reportRequest('new-vs-returning', filters),
  // Frequent Patients: { from, to, minVisits } -> patients by number of visits, most first.
  getFrequentPatients: async (filters) => reportRequest('frequent-patients', filters),
  // Every patient on file; needs the Patient Register permission on top of the OPD group.
  getPatientRegister: async (params) => reportRequest('patient-register', params),
  // Attendance (the fifth report group). The register is the same payload the attendance board
  // draws, served to people who hold Reports rather than the board itself.
  getAttendanceSummary: async (filters) => reportRequest('attendance-summary', filters),
  getAttendanceFlagged: async (filters) => reportRequest('attendance-flagged', filters),
  getAttendanceLate: async (filters) => reportRequest('attendance-late', filters),
  getAttendancePositions: async (filters) => reportRequest('attendance-positions', filters),
  getLeaveBalances: async (filters) => reportRequest('leave-balances', filters),
  getAttendanceRegister: async (month) => reportRequest('attendance-register', { month }),
  // Payroll sits in Finance and needs its own permission: it carries salaries.
  getPayroll: async (month) => reportRequest('payroll', { month }),
  lockPayroll: async (body) =>
    apiRequest('/reports/payroll/lock', { method: 'POST', body: JSON.stringify(body) }),
  unlockPayroll: async (month) =>
    apiRequest(`/reports/payroll/lock/${encodeURIComponent(month)}`, { method: 'DELETE' }),
  getAgeGender: async (filters) => reportRequest('age-gender', filters),
  // No filters: a shared CNIC is only visible when the whole patient base is in scope.
  getSharedCnic: async () => reportRequest('shared-cnic'),
  // Same reason; limit caps the returned worklist, not the totals it reports.
  getDuplicatePatients: async (filters) => reportRequest('duplicate-patients', filters),
  // Row drill-down, shared by every report. `spec.type` picks the drill; the rest are its params.
  getDrill: async (spec) => reportRequest('drill', spec),
  // Patient Profile. The search takes any mix of the form's fields (blank ones are dropped); the
  // profile takes { patientId, from?, to? } and leaves out the sections the user may not see.
  searchPatients: async (criteria) => reportRequest('patient-search', criteria),
  getPatientProfile: async (params) => reportRequest('patient-profile', params),
  // Medicine Profile. The catalogue is every medicine and supply with its stock and status (a few
  // hundred rows, filtered on screen); the profile takes { medicineId, from?, to? }.
  getMedicineCatalogue: async () => reportRequest('medicine-catalogue'),
  getMedicineProfile: async (params) => reportRequest('medicine-profile', params),
  // Clinical
  getTopDiagnoses: async (filters) => reportRequest('top-diagnoses', filters),
  getTopMedicines: async (filters) => reportRequest('top-medicines', filters),
  getTopLabTests: async (filters) => reportRequest('top-lab-tests', filters),
  // Inventory
  getStockValuation: async (window) => reportRequest('stock-valuation', { window }),
  getConsumption: async (filters) => reportRequest('consumption', filters),
  getExpiryWatch: async () => reportRequest('expiry-watch'),
  getVendorPurchases: async (filters) => reportRequest('vendor-purchases', filters),
  getPriceChanges: async (filters) => reportRequest('price-changes', filters),
  getGenericPrices: async () => reportRequest('generic-prices'),
  getOrderDeliveries: async (filters) => reportRequest('order-deliveries', filters),
};

// Clinic-wide operational toggles. The Nurse Station is NOT one of them any more — it is set per
// doctor under Doctor Settings; `nurseStationInUse` here only reports whether any doctor uses one,
// which is what decides if the Nurse Station menu is worth showing.
export const clinicalSettingsAPI = {
  get: async () => apiRequest('/clinical-settings'),
  // The two consumables a bedside sugar check uses, with their stock, for the doctor's warning.
  bedsideSugar: async () => apiRequest('/clinical-settings/bedside-sugar'),
  // Global default profit markup % applied to medicine selling prices.
  setProfitMarkup: async (percentage) =>
    apiRequest('/clinical-settings/profit-markup', {
      method: 'PATCH',
      body: JSON.stringify({ percentage }),
    }),
  setDeletedPatientRetention: async (days) =>
    apiRequest('/clinical-settings/deleted-patient-retention', {
      method: 'PATCH',
      body: JSON.stringify({ days }),
    }),
  // When on, the prescription workspace shows a "Save Template" button.
  setAllowSaveTemplate: async (enabled) =>
    apiRequest('/clinical-settings/allow-save-template', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),
  // Suspends or resumes the first-visit medical history. Off stops the asking only: histories
  // already taken stay on the record, and the question catalogue stays editable.
  setIntakeFormEnabled: async (enabled) =>
    apiRequest('/clinical-settings/intake-form', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),
  // Billing counter settings. Any subset of { roundingRule, receiptAutoPrint, receiptPaper };
  // returns all three as billingRoundingRule, billingReceiptAutoPrint, billingReceiptPaper.
  updateBilling: async (body) =>
    apiRequest('/clinical-settings/billing', {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    }),
};

// Hospital locations + per-location timezone. The default location's timezone is the clinic-wide
// clock (the backend applies it JVM-wide so all new timestamps use it).
export const locationAPI = {
  getAll: async () => apiRequest('/locations'),
  getActive: async () => apiRequest('/locations?activeOnly=true'),
  // Lightweight bootstrap read: { timezone, locationId, locationName }.
  getTimezone: async () => apiRequest('/locations/timezone'),
  create: async (data) =>
    apiRequest('/locations', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) =>
    apiRequest(`/locations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  setTimezone: async (id, timezone) =>
    apiRequest(`/locations/${id}/timezone`, {
      method: 'PATCH',
      body: JSON.stringify({ timezone }),
    }),
  setDefault: async (id) => apiRequest(`/locations/${id}/default`, { method: 'PATCH' }),
  remove: async (id) => apiRequest(`/locations/${id}`, { method: 'DELETE' }),
};

// Attendance (backend: AttendanceController.java). Every punch carries a selfie and the phone's
// position; the server stamps the time. Refusals are 409 with a code: ALREADY_PUNCHED_IN,
// NOT_PUNCHED_IN, ENTRY_OPEN, ENTRY_EXISTS, REASON_REQUIRED.
const attendancePunch = async (side, photo, position) => {
  const token = localStorage.getItem('clinic_auth_token');
  const formData = new FormData();
  formData.append('photo', photo, `${side}.jpg`);
  if (position && position.latitude != null && position.longitude != null) {
    formData.append('latitude', String(position.latitude));
    formData.append('longitude', String(position.longitude));
    if (position.accuracy != null) formData.append('accuracy', String(Math.round(position.accuracy)));
  }
  const response = await serverFetch(`${API_BASE_URL}/attendance/punch-${side}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401 || response.status === 403) {
    if (!signOutUnlessTokenReplaced(token)) throw new Error('Please try again.');
    throw new Error('Session expired. Please log in again.');
  }
  const data = await response.json().catch(() => ({ error: 'Punch failed' }));
  if (!response.ok) {
    const err = new Error(data.message || data.error || 'Punch failed');
    err.code = data.code || data.error;
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
};

// A leave request and its optional attachment go up together, the way a stock receipt and its bill
// do: the JSON in a `payload` part with an explicit type, the file alongside it.
const attendanceLeave = async (path, payload, attachment) => {
  const token = localStorage.getItem('clinic_auth_token');
  const formData = new FormData();
  formData.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  if (attachment) formData.append('attachment', attachment, attachment.name || 'attachment');
  const response = await serverFetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401 || response.status === 403) {
    if (!signOutUnlessTokenReplaced(token)) throw new Error('Please try again.');
    throw new Error('Session expired. Please log in again.');
  }
  const data = await response.json().catch(() => ({ error: 'The request could not be sent' }));
  if (!response.ok) {
    const err = new Error(data.message || data.error || 'The request could not be sent');
    err.code = data.code || data.error;
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
};

export const attendanceAPI = {
  // GET /api/attendance/settings — work days, hours, grace and thresholds.
  getSettings: async () => apiRequest('/attendance/settings'),
  // PATCH /api/attendance/settings — any subset; returns all.
  updateSettings: async (body) =>
    apiRequest('/attendance/settings', { method: 'PATCH', body: JSON.stringify(body || {}) }),

  // POST /api/attendance/punch-code — a one-time code, good for three minutes, that moves this
  // punch to the person's own phone (it scans to /punch.html). The phone needs no sign-in: the code
  // is the key, so it is single use and short lived.
  // `forUserId` makes the code for a colleague instead, which any signed-in user may do when
  // somebody's computer is dead; it needs a reason, and the entry records who set it up.
  punchCode: async (side, forUser) =>
    apiRequest('/attendance/punch-code', {
      method: 'POST',
      body: JSON.stringify({ side, ...(forUser || {}) }),
    }),

  // GET /api/attendance/colleagues — active people to pick from, name and role only.
  colleagues: async () => apiRequest('/attendance/colleagues'),

  // GET /api/punch/{token} — the public lookup the phone uses. The screen showing a code polls it to
  // notice when the phone has spent it: a used code answers { valid: false, reason: 'USED' }.
  //
  // Deliberately a plain fetch, NOT apiRequest: that signs the user out of the whole application on
  // a 401, and a background poll about a punch code must never be able to cost somebody their
  // session. The endpoint is public, so there is no token to send either. Anything that goes wrong
  // answers null and the next poll tries again.
  punchCodeStatus: async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/punch/${encodeURIComponent(token)}`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  },

  // GET /api/attendance/mine — { open, today, week, weekHours, settings, hospital }
  mine: async () => apiRequest('/attendance/mine'),
  // POST /api/attendance/punch-in|out — multipart: photo + latitude/longitude/accuracy.
  punchIn: async (photo, position) => attendancePunch('in', photo, position),
  punchOut: async (photo, position) => attendancePunch('out', photo, position),

  // GET /api/attendance/today — every active user with today's status.
  today: async () => apiRequest('/attendance/today'),
  // GET /api/attendance/register?month=YYYY-MM — the month grid.
  register: async (month) => apiRequest(`/attendance/register?month=${encodeURIComponent(month)}`),
  // GET /api/attendance/entries?from&to&userId
  entries: async ({ from, to, userId } = {}) => {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    if (userId) q.set('userId', userId);
    return apiRequest(`/attendance/entries?${q.toString()}`);
  },
  // GET /api/attendance/flagged?all=
  flagged: async (all = false) => apiRequest(`/attendance/flagged?all=${all ? 'true' : 'false'}`),
  // GET /api/attendance/corrections-due — auto-closed entries nobody has corrected yet.
  correctionsDue: async () => apiRequest('/attendance/corrections-due'),

  // POST /api/attendance/entries/{id}/review — { decision: 'ACCEPTED' | 'REJECTED', note }
  review: async (id, decision, note) =>
    apiRequest(`/attendance/entries/${id}/review`, { method: 'POST', body: JSON.stringify({ decision, note }) }),
  // POST /api/attendance/entries/{id}/correct — { clockInAt, clockOutAt, reason }
  correct: async (id, body) =>
    apiRequest(`/attendance/entries/${id}/correct`, { method: 'POST', body: JSON.stringify(body) }),
  // POST /api/attendance/entries — a manual entry for a missed day.
  createManual: async (body) =>
    apiRequest('/attendance/entries', { method: 'POST', body: JSON.stringify(body) }),
  // GET /api/attendance/entries/{id}/corrections
  corrections: async (id) => apiRequest(`/attendance/entries/${id}/corrections`),

  // ---------------------------------------------------------------- holidays
  // Hospital-wide days off. Everyone reads them; only MANAGE_HOLIDAYS writes.
  holidays: async (year) => apiRequest(`/attendance/holidays${year ? `?year=${year}` : ''}`),
  createHoliday: async (body) =>
    apiRequest('/attendance/holidays', { method: 'POST', body: JSON.stringify(body) }),
  updateHoliday: async (id, body) =>
    apiRequest(`/attendance/holidays/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteHoliday: async (id) => apiRequest(`/attendance/holidays/${id}`, { method: 'DELETE' }),

  // Working hours are a user detail now, set on the Add/Edit User form (owner, 2026-10-05):
  // userAPI.update carries `workingHours`, and the attendance endpoints for them are gone.
  // When each person is expected at the hospital. No schedule means the hospital default.

  // ---------------------------------------------------------------- leave
  leaveTypes: async (all = false) => apiRequest(`/attendance/leave/types?all=${all ? 'true' : 'false'}`),
  createLeaveType: async (body) =>
    apiRequest('/attendance/leave/types', { method: 'POST', body: JSON.stringify(body) }),
  updateLeaveType: async (id, body) =>
    apiRequest(`/attendance/leave/types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteLeaveType: async (id) => apiRequest(`/attendance/leave/types/${id}`, { method: 'DELETE' }),

  // POST /api/attendance/leave/preview — how many days that range costs, and what is left. Writes nothing.
  leavePreview: async (body) =>
    apiRequest('/attendance/leave/preview', { method: 'POST', body: JSON.stringify(body) }),

  // POST /api/attendance/leave — multipart so the request and its attachment (a sick note) commit
  // together. `/leave/for` records leave for someone else, already approved.
  requestLeave: async (payload, attachment) => attendanceLeave('/attendance/leave', payload, attachment),
  recordLeaveFor: async (payload, attachment) => attendanceLeave('/attendance/leave/for', payload, attachment),

  myLeave: async () => apiRequest('/attendance/leave/mine'),
  cancelLeave: async (id) => apiRequest(`/attendance/leave/${id}/cancel`, { method: 'POST' }),
  decideLeave: async (id, decision, note) =>
    apiRequest(`/attendance/leave/${id}/decide`, { method: 'POST', body: JSON.stringify({ decision, note }) }),
  leaveRequests: async ({ status, from, to, userId } = {}) => {
    const q = new URLSearchParams();
    if (status) q.set('status', status);
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    if (userId) q.set('userId', userId);
    const qs = q.toString();
    return apiRequest(`/attendance/leave${qs ? `?${qs}` : ''}`);
  },
  leaveCalendar: async (month) => apiRequest(`/attendance/leave/calendar?month=${encodeURIComponent(month)}`),
};

// Complaints (backend: ComplaintController). Patients send these from a QR poster on the wall, to a
// public endpoint under /api/public-complaints; everything here is the administrators' side.
export const complaintAPI = {
  // GET /api/complaints?status=&category=&from=&to=
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'ALL') params.append(k, v);
    });
    const qs = params.toString();
    return apiRequest(`/complaints${qs ? `?${qs}` : ''}`);
  },
  // GET /api/complaints/stats — { newCount } for the sidebar badge and the sign-in notice.
  getStats: async () => apiRequest('/complaints/stats'),
  // PATCH /api/complaints/{id} — { status, resolutionNote }; closing one needs a note.
  update: async (id, body) =>
    apiRequest(`/complaints/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // DELETE /api/complaints/{id} — for spam; takes the photo with it.
  remove: async (id) => apiRequest(`/complaints/${id}`, { method: 'DELETE' }),

  // ---- the workflow ----------------------------------------------------------------------------
  // GET /api/complaints/{id} — one complaint and everything that has happened to it.
  get: async (id) => apiRequest(`/complaints/${id}`),
  // GET /api/complaints/mine — assigned to me. Needs no complaints permission: being the assignee
  // IS the permission, and it shows that one complaint and nothing else.
  mine: async () => apiRequest('/complaints/mine'),
  // GET /api/complaints/assignable — { id, fullName, role } only, never whole user records.
  assignable: async () => apiRequest('/complaints/assignable'),
  // POST /api/complaints/{id}/assign — { userId | null, note }. A null userId hands it back.
  assign: async (id, body) =>
    apiRequest(`/complaints/${id}/assign`, { method: 'POST', body: JSON.stringify(body) }),
  // POST /api/complaints/{id}/events — { type: 'CONTACT' | 'NOTE', channel, contactOutcome, note }.
  // Append-only: there is no edit and no delete, because the trail is the point.
  addEvent: async (id, body) =>
    apiRequest(`/complaints/${id}/events`, { method: 'POST', body: JSON.stringify(body) }),
  // POST /api/complaints/{id}/triage — { severity, concernsUserId }; either may be null.
  triage: async (id, body) =>
    apiRequest(`/complaints/${id}/triage`, { method: 'POST', body: JSON.stringify(body) }),
  // POST /api/complaints/{id}/informed — { channel, note }. Recording that we closed the loop.
  informed: async (id, body) =>
    apiRequest(`/complaints/${id}/informed`, { method: 'POST', body: JSON.stringify(body) }),
  // GET /api/complaints/overview?from=&to= — the Overview tab's figures.
  overview: async ({ from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    return apiRequest(`/complaints/overview${qs ? `?${qs}` : ''}`);
  },
};

export default {
  patient: patientAPI,
  visit: visitAPI,
  doctor: doctorAPI,
  medicalService: medicalServiceAPI,
  family: familyAPI,
  dashboard: dashboardAPI,
  user: userAPI,
  vendor: vendorAPI,
  medicine: medicineAPI,
  asset: assetAPI,
  medicineOrder: medicineOrderAPI,
  prescription: prescriptionAPI,
  rolePermission: rolePermissionAPI,
  auth: authAPI,
  invoice: invoiceAPI,
  cashDrawer: cashDrawerAPI,
  patientDocument: patientDocumentAPI,
  expense: expenseAPI,
  stockReceipt: stockReceiptAPI,
  location: locationAPI,
  attendance: attendanceAPI,
  complaint: complaintAPI,
};